import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu as MenuIcon, X, Flame, MapPin, Phone, User, LogOut, Package, Loader2, Ticket, Gift, Download, Search, Plus, Navigation, LocateFixed, Star, Clock, Tag, Percent, Check, UtensilsCrossed, Heart, Store, Truck, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api, { getImageUrl } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { checkInstallPrompt, promptInstall } from '@/serviceWorkerRegistration';
import Footer from '../components/Footer';
import OrderStatusTracker from '../components/OrderStatusTracker';

const CustomerApp = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const outletId = searchParams.get('outlet') || 'main';
  
  const [products, setProducts] = useState([]);
  // Initialize cart from localStorage
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem('fishOnFireCart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch {
      return [];
    }
  });
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('fishOnFireCart', JSON.stringify(cart));
  }, [cart]);
  
  // PWA Install state
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  
  // Location & delivery state
  const [userLocation, setUserLocation] = useState(null);
  const [deliveryAvailable, setDeliveryAvailable] = useState(null);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [locationPermission, setLocationPermission] = useState('prompt');
  const [locationError, setLocationError] = useState(null); // "no_outlet_in_range" | "all_outlets_closed" | null
  const [outletStatusTimestamp, setOutletStatusTimestamp] = useState(null); // For polling
  
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [loginPhone, setLoginPhone] = useState('');
  const [loginOtp, setLoginOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const otpInputRef = useRef(null);
  
  // Referral code during signup
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [signupReferralCode, setSignupReferralCode] = useState('');
  const [loyaltyConfig, setLoyaltyConfig] = useState(null);
  
  // Auto-focus OTP input when OTP is sent
  useEffect(() => {
    if (otpSent && otpInputRef.current) {
      setTimeout(() => otpInputRef.current?.focus(), 100);
    }
  }, [otpSent]);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [showAvailableCoupons, setShowAvailableCoupons] = useState(false);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  
  // Loyalty points state
  const [loyaltyBalance, setLoyaltyBalance] = useState(null);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [pointsDiscount, setPointsDiscount] = useState(0);
  
  // Dynamic categories from admin
  const [categories, setCategories] = useState(['All']);
  
  // Favorites state
  const [favorites, setFavorites] = useState(new Set());
  const [showFavorites, setShowFavorites] = useState(false);
  
  // Quick Reorder state
  const [recentOrders, setRecentOrders] = useState([]);
  const [loadingRecentOrders, setLoadingRecentOrders] = useState(false);
  const [reorderingItems, setReorderingItems] = useState(null);
  
  // Add-on selection state for product customization
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedCustomizations, setSelectedCustomizations] = useState({});
  const [itemNotes, setItemNotes] = useState('');
  const [addonProducts, setAddonProducts] = useState([]); // Add-on only products for cross-sell
  const [selectedAddonProducts, setSelectedAddonProducts] = useState([]); // Selected add-on products
  
  // Network status state
  const [networkStatus, setNetworkStatus] = useState('good'); // 'good' | 'slow' | 'offline'
  const [showNetworkWarning, setShowNetworkWarning] = useState(false);
  
  // Loading states with messages
  const [loadingMessage, setLoadingMessage] = useState('');
  
  // Offline order queue
  const [offlineOrders, setOfflineOrders] = useState(() => {
    const saved = localStorage.getItem('offlineOrders');
    return saved ? JSON.parse(saved) : [];
  });
  const [showOfflineOrders, setShowOfflineOrders] = useState(false);
  const [syncingOrders, setSyncingOrders] = useState(false);
  
  // Payment retry state
  const [paymentRetryCount, setPaymentRetryCount] = useState(0);
  const MAX_PAYMENT_RETRIES = 3;

  // Hero/Banner settings from admin
  const [heroSettings, setHeroSettings] = useState({
    hero_headline: 'Where Heat Meets The Sea',
    hero_tagline: 'Premium seafood grilled to perfection. Fast, fresh, and on fire.',
    hero_image: null
  });
  
  // Site settings including fees
  const [siteSettings, setSiteSettings] = useState({
    delivery_fee: 40,
    platform_fee: 5,
    free_delivery_threshold: 500,
    contact_phone: '',
    contact_whatsapp: '',
    contact_email: '',
    operating_hours: '10 AM - 12 AM'
  });
  
  // Outlet accepting orders status
  const [outletStatus, setOutletStatus] = useState({
    isAcceptingOrders: true,
    orderingStartTime: '09:00',
    orderingEndTime: '22:00'
  });
  
  // Bill details expansion state
  const [showBillDetails, setShowBillDetails] = useState(false);
  
  // Payment settings from admin
  const [paymentSettings, setPaymentSettings] = useState({
    cod_enabled: true,
    upi_enabled: true,
    razorpay_enabled: true
  });
  
  // Saved addresses state
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [saveThisAddress, setSaveThisAddress] = useState(false);
  const [addressLabel, setAddressLabel] = useState('Home');
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 20.5937, lng: 78.9629 }); // Default to India center
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  
  // Checkout form state
  const [checkoutData, setCheckoutData] = useState({
    customer_name: '',
    customer_phone: '+91',
    customer_address: '',
    flat_house: '',
    street_area: '',
    landmark: '',
    city: '',
    pincode: '',
    latitude: null,
    longitude: null,
    order_type: 'delivery',
    payment_method: '', // Will be set based on enabled options
    selected_outlet_id: outletId
  });
  
  // Google Places Autocomplete ref
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  
  // WebSocket for real-time stock updates
  const wsRef = useRef(null);

  // Global fix for Google Places dropdown inside modals
  useEffect(() => {
    const handlePacContainerClick = (e) => {
      // Check if click is on pac-container or its children
      if (e.target.closest('.pac-container')) {
        e.stopPropagation();
      }
    };
    
    // Use capture phase to intercept before dialog
    document.addEventListener('mousedown', handlePacContainerClick, true);
    document.addEventListener('touchstart', handlePacContainerClick, true);
    
    return () => {
      document.removeEventListener('mousedown', handlePacContainerClick, true);
      document.removeEventListener('touchstart', handlePacContainerClick, true);
    };
  }, []);

  // Simple offline detection only (removed aggressive network speed warnings)
  useEffect(() => {
    const handleOnline = () => {
      setNetworkStatus('good');
      setShowNetworkWarning(false);
    };
    
    const handleOffline = () => {
      setNetworkStatus('offline');
      setShowNetworkWarning(true);
      toast.error('No internet connection');
    };
    
    // Set initial status
    if (!navigator.onLine) {
      setNetworkStatus('offline');
      setShowNetworkWarning(true);
    }
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchOutlets();
    fetchCategories();
    fetchHeroSettings();
    fetchPaymentSettings();
    fetchLoyaltyConfig();
    requestLocationPermission();
    requestNotificationPermission();
    checkAuthStatus();
    
    // Fallback: If location permission not decided in 5 seconds, set default outlet
    const locationTimeout = setTimeout(() => {
      if (locationPermission === 'prompt') {
        setLocationPermission('timeout');
      }
    }, 5000);
    
    // Check for PWA install prompt
    checkInstallPrompt(setInstallPrompt);
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }
    
    // Poll for outlet status changes every 30 seconds
    const statusPollInterval = setInterval(() => {
      pollOutletStatus();
    }, 30000);
    
    return () => {
      clearTimeout(locationTimeout);
      clearInterval(statusPollInterval);
    };
  }, []);

  // Poll outlet status to detect changes (outlet on/off, stock updates)
  const pollOutletStatus = async () => {
    try {
      const response = await api.get('/outlets/status-check');
      const newTimestamp = response.data.timestamp;
      
      // Check if any outlet status changed
      if (outletStatusTimestamp && response.data.outlets) {
        const currentOutlets = response.data.outlets;
        
        // Find the currently selected outlet
        if (selectedOutlet) {
          const updatedOutlet = currentOutlets.find(o => o.id === selectedOutlet.id);
          
          // Check if selected outlet went offline
          if (updatedOutlet && !updatedOutlet.is_accepting_orders && outletStatus.isAcceptingOrders) {
            toast.error(`${selectedOutlet.name} is no longer accepting orders. Please select another location.`, {
              duration: 5000
            });
            setOutletStatus(prev => ({ ...prev, isAcceptingOrders: false }));
            
            // Try to find another active outlet within range
            await findAlternativeOutlet(currentOutlets);
          }
          // Check if outlet came back online
          else if (updatedOutlet && updatedOutlet.is_accepting_orders && !outletStatus.isAcceptingOrders) {
            toast.success(`${selectedOutlet.name} is now accepting orders!`);
            setOutletStatus(prev => ({ ...prev, isAcceptingOrders: true }));
          }
        }
        
        // Update outlets list
        setOutlets(prev => prev.map(o => {
          const updated = currentOutlets.find(u => u.id === o.id);
          return updated ? { ...o, is_accepting_orders: updated.is_accepting_orders } : o;
        }));
      }
      
      setOutletStatusTimestamp(newTimestamp);
    } catch (error) {
      console.error('Error polling outlet status:', error);
    }
  };

  // Find an alternative outlet when current one goes offline
  const findAlternativeOutlet = async (currentOutlets) => {
    if (!userLocation) return;
    
    // Find active outlets within delivery range
    const activeOutlets = currentOutlets.filter(o => o.is_accepting_orders);
    
    for (const outlet of activeOutlets) {
      if (outlet.latitude && outlet.longitude) {
        const distance = calculateDistance(
          parseFloat(outlet.latitude), parseFloat(outlet.longitude),
          userLocation.latitude, userLocation.longitude
        );
        const radius = outlet.delivery_radius_km || 5;
        
        if (distance <= radius) {
          toast.info(`Switched to ${outlet.name} (${distance.toFixed(1)}km away)`);
          const fullOutlet = outlets.find(o => o.id === outlet.id);
          if (fullOutlet) {
            setSelectedOutlet({ ...fullOutlet, is_accepting_orders: true });
            setCheckoutData(prev => ({ ...prev, selected_outlet_id: outlet.id }));
            fetchProducts(outlet.id);
            fetchOutletStatus(outlet.id);
          }
          return;
        }
      }
    }
    
    // No alternative found
    setLocationError('all_outlets_closed');
  };

  useEffect(() => {
    if (outlets.length > 0 && !selectedOutlet && (locationPermission !== 'prompt')) {
      // Only set default outlet if no outlet has been selected (by location detection)
      // And only if location permission has been determined (granted, denied, or timeout)
      
      // Prefer an outlet that is accepting orders
      let outlet = outlets.find(o => o.id === outletId && o.is_accepting_orders !== false);
      
      // If that outlet is not accepting, find any that is
      if (!outlet || outlet.is_accepting_orders === false) {
        outlet = outlets.find(o => o.is_accepting_orders !== false);
      }
      
      // Fall back to the first outlet if none are accepting
      if (!outlet) {
        outlet = outlets.find(o => o.id === outletId) || outlets[0];
      }
      
      setSelectedOutlet(outlet);
      setCheckoutData(prev => ({ ...prev, selected_outlet_id: outlet.id }));
    }
  }, [outlets, outletId, selectedOutlet, locationPermission]);

  // Refetch products when selected outlet changes
  useEffect(() => {
    if (selectedOutlet?.id) {
      fetchProducts(selectedOutlet.id);
      fetchOutletStatus(selectedOutlet.id);
      connectWebSocket(selectedOutlet.id);
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [selectedOutlet?.id]);

  // WebSocket connection for real-time stock updates
  const connectWebSocket = useCallback((outletId) => {
    if (!outletId) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    // Construct WebSocket URL
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsHost = backendUrl.replace(/^https?:\/\//, '').replace(/\/api$/, '');
    const wsUrl = `${wsProtocol}//${wsHost}/ws/stock/${outletId}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('WebSocket connected for stock updates');
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'stock_update') {
            // Update product stock in state
            setProducts(prev => prev.map(p => ({
              ...p,
              stock: data.stock_data[p.id] !== undefined ? data.stock_data[p.id] : p.stock
            })));
            
            // Check cart items for out of stock
            setCart(prev => {
              let hasChanges = false;
              const updatedCart = prev.map(item => {
                const newStock = data.stock_data[item.product_id];
                if (newStock !== undefined && newStock < item.quantity) {
                  hasChanges = true;
                  if (newStock === 0) {
                    toast.error(`${item.product_name} is now out of stock and removed from cart`);
                    return null;
                  } else {
                    toast.warning(`${item.product_name} quantity reduced to ${newStock} (max available)`);
                    return { ...item, quantity: newStock, max_stock: newStock };
                  }
                }
                return item;
              }).filter(Boolean);
              
              return hasChanges ? updatedCart : prev;
            });
          }
        } catch (e) {
          console.error('WebSocket message parse error:', e);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      ws.onclose = () => {
        console.log('WebSocket disconnected');
        // Attempt to reconnect after 5 seconds
        setTimeout(() => {
          if (selectedOutlet?.id) {
            connectWebSocket(selectedOutlet.id);
          }
        }, 5000);
      };
      
      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }, [selectedOutlet]);

  // Fetch outlet status (accepting orders, operating hours)
  const fetchOutletStatus = async (outletId) => {
    try {
      const response = await api.get(`/outlets/${outletId}/status`);
      setOutletStatus({
        isAcceptingOrders: response.data.is_accepting_orders !== false,
        orderingStartTime: response.data.ordering_start_time || '09:00',
        orderingEndTime: response.data.ordering_end_time || '22:00'
      });
    } catch (error) {
      console.error('Error fetching outlet status:', error);
    }
  };

  // Initialize Google Places Autocomplete when checkout is shown and delivery is selected
  useEffect(() => {
    let pacContainerObserver = null;
    
    const initAutocomplete = () => {
      // Only initialize if checkout is shown, delivery is selected, and input exists
      // Also check if address form is visible (no saved addresses OR showNewAddressForm)
      const addressFormVisible = savedAddresses.length === 0 || showNewAddressForm;
      if (!showCheckout || checkoutData.order_type !== 'delivery' || !addressFormVisible || !addressInputRef.current) {
        return;
      }
      
      // Check if Google Maps is loaded
      if (!window.google?.maps?.places) {
        console.log('Google Maps not loaded yet, retrying...');
        return;
      }
      
      // Clean up existing autocomplete
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      
      try {
        console.log('Initializing Google Places Autocomplete...');
        const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
          componentRestrictions: { country: 'in' },
          fields: ['address_components', 'geometry', 'formatted_address', 'name'],
          types: ['establishment', 'geocode']
        });

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          console.log('Place selected:', place);
          
          if (!place || !place.geometry) {
            console.log('No geometry in place');
            toast.error('Please select a location from the dropdown');
            return;
          }
          
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          
          // Extract address components
          let streetNumber = '';
          let route = '';
          let sublocality = '';
          let locality = '';
          let city = '';
          let pincode = '';
          
          if (place.address_components) {
            for (const component of place.address_components) {
              const types = component.types;
              if (types.includes('street_number')) {
                streetNumber = component.long_name;
              }
              if (types.includes('route')) {
                route = component.long_name;
              }
              if (types.includes('sublocality_level_1') || types.includes('sublocality') || types.includes('neighborhood')) {
                sublocality = component.long_name;
              }
              if (types.includes('locality')) {
                locality = component.long_name;
              }
              if (types.includes('administrative_area_level_2') || types.includes('administrative_area_level_1')) {
                if (!city) city = component.long_name;
              }
              if (types.includes('postal_code')) {
                pincode = component.long_name;
              }
            }
          }
          
          console.log('Extracted data:', { streetNumber, route, sublocality, locality, city, pincode, lat, lng });
          
          // Update checkout data with parsed address
          setCheckoutData(prev => ({
            ...prev,
            flat_house: place.name || streetNumber || '',
            street_area: [route, sublocality].filter(Boolean).join(', ') || sublocality || '',
            city: locality || city || '',
            pincode: pincode || '',
            latitude: lat,
            longitude: lng,
            customer_address: place.formatted_address || ''
          }));
          
          toast.success('Address selected with location!');
        });
        
        autocompleteRef.current = autocomplete;
        console.log('Google Places Autocomplete initialized successfully');
        
        // Fix for pac-container click events being blocked by dialog
        // Watch for pac-container and add event listeners
        pacContainerObserver = new MutationObserver((mutations) => {
          const pacContainer = document.querySelector('.pac-container');
          if (pacContainer && !pacContainer.dataset.fixApplied) {
            pacContainer.dataset.fixApplied = 'true';
            
            // Prevent mousedown from closing dialog
            pacContainer.addEventListener('mousedown', (e) => {
              e.stopPropagation();
            });
            
            // Prevent touchstart from closing dialog (mobile)
            pacContainer.addEventListener('touchstart', (e) => {
              e.stopPropagation();
            });
            
            console.log('Applied click fix to pac-container');
          }
        });
        
        pacContainerObserver.observe(document.body, { childList: true, subtree: true });
        
      } catch (error) {
        console.error('Error initializing Google Places:', error);
      }
    };

    // Try immediately
    initAutocomplete();
    
    // Also try after a short delay in case Google Maps is still loading
    const timeoutId = setTimeout(initAutocomplete, 500);
    const timeoutId2 = setTimeout(initAutocomplete, 1500);
    
    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(timeoutId2);
      if (pacContainerObserver) {
        pacContainerObserver.disconnect();
      }
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [showCheckout, checkoutData.order_type, showNewAddressForm, savedAddresses.length]);

  // Check if user is already logged in
  const checkAuthStatus = async () => {
    const token = localStorage.getItem('customerToken');
    const savedCustomer = localStorage.getItem('customerData');
    
    if (token && savedCustomer) {
      try {
        const [profileRes, loyaltyRes] = await Promise.all([
          api.get('/customers/me'),
          api.get('/loyalty/balance').catch(() => ({ data: null }))
        ]);
        
        setCustomer(profileRes.data);
        setIsLoggedIn(true);
        setLoyaltyBalance(loyaltyRes.data);
        
        // Load saved addresses
        if (profileRes.data?.saved_addresses?.length > 0) {
          setSavedAddresses(profileRes.data.saved_addresses);
          // Auto-select default address
          const defaultAddr = profileRes.data.saved_addresses.find(a => a.is_default);
          if (defaultAddr) {
            selectSavedAddress(defaultAddr);
          } else {
            // Select first address if no default
            selectSavedAddress(profileRes.data.saved_addresses[0]);
          }
        } else {
          // No saved addresses - show new address form
          setShowNewAddressForm(true);
          setCheckoutData(prev => ({
            ...prev,
            customer_phone: profileRes.data.phone,
            customer_name: profileRes.data.name || ''
          }));
        }
      } catch (error) {
        // Token expired or invalid
        localStorage.removeItem('customerToken');
        localStorage.removeItem('customerData');
      }
    }
  };

  // Fetch loyalty balance
  const fetchLoyaltyBalance = async () => {
    if (!isLoggedIn) return;
    try {
      const response = await api.get('/loyalty/balance');
      setLoyaltyBalance(response.data);
    } catch (error) {
      console.error('Error fetching loyalty balance:', error);
    }
  };

  // Handle PWA install
  const handleInstallClick = async () => {
    const installed = await promptInstall(installPrompt);
    if (installed) {
      setIsInstalled(true);
      setInstallPrompt(null);
      toast.success('App installed successfully!');
    }
  };

  // Handle points redemption
  const handlePointsChange = (points) => {
    const numPoints = parseInt(points) || 0;
    const maxPoints = loyaltyBalance?.balance || 0;
    const clampedPoints = Math.min(Math.max(0, numPoints), maxPoints);
    
    // Use dynamic rupees_per_point from settings
    const rupeesPerPoint = loyaltyConfig?.rupees_per_point || loyaltyBalance?.settings?.rupees_per_point || 0.25;
    setPointsToRedeem(clampedPoints);
    setPointsDiscount(clampedPoints * rupeesPerPoint);
  };

  const handleUseAllPoints = () => {
    if (loyaltyBalance?.can_redeem) {
      handlePointsChange(loyaltyBalance.balance);
    }
  };

  const handleClearPoints = () => {
    setPointsToRedeem(0);
    setPointsDiscount(0);
  };

  // Send OTP
  const handleSendOtp = async () => {
    if (!loginPhone || loginPhone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    
    const fullPhone = `+91${loginPhone}`;
    setAuthLoading(true);
    setLoadingMessage('Sending OTP...');
    
    // Show slow network warning if detected
    if (networkStatus === 'slow') {
      toast.info('Slow connection detected. This may take a moment...', { duration: 3000 });
    }
    
    try {
      const response = await api.post('/auth/send-otp', { phone: fullPhone });
      
      // Check if demo OTP is returned (for testing environments)
      if (response.data.demo_otp) {
        toast.success(`Demo OTP: ${response.data.demo_otp}`, { duration: 10000 });
        setLoginOtp(response.data.demo_otp); // Auto-fill for convenience in demo
      } else {
        toast.success('OTP sent to your mobile number!');
      }
      
      setOtpSent(true);
    } catch (error) {
      console.error('Error sending OTP:', error);
      if (error.response?.status === 429) {
        toast.error(error.response.data.detail || 'Too many requests. Please wait.');
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Request timed out. Please check your connection and try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to send OTP. Please try again.');
      }
    } finally {
      setAuthLoading(false);
      setLoadingMessage('');
    }
  };

  // Verify OTP and login
  const handleVerifyOtp = async () => {
    if (!loginOtp || loginOtp.length < 4) {
      toast.error('Please enter the OTP');
      return;
    }
    
    const fullPhone = `+91${loginPhone}`;
    setAuthLoading(true);
    try {
      const response = await api.post('/auth/verify-otp', {
        phone: fullPhone,
        otp: loginOtp
      });
      
      // Save token and customer data
      localStorage.setItem('customerToken', response.data.token);
      localStorage.setItem('customerData', JSON.stringify(response.data.customer));
      
      setCustomer(response.data.customer);
      setIsLoggedIn(true);
      setShowLogin(false);
      
      // Apply referral code if provided (for new customers)
      if (signupReferralCode && response.data.is_new_customer) {
        try {
          await api.post('/loyalty/apply-referral', { referral_code: signupReferralCode });
          const bonusPoints = loyaltyConfig?.referral_bonus_referee || 50;
          toast.success(`Referral code applied! You got ${bonusPoints} bonus points! 🎁`);
        } catch (refError) {
          console.log('Could not apply referral code:', refError.response?.data?.detail);
        }
      }
      
      // Fetch loyalty balance
      try {
        const loyaltyRes = await api.get('/loyalty/balance');
        setLoyaltyBalance(loyaltyRes.data);
      } catch (e) {
        console.log('Could not fetch loyalty balance');
      }
      
      // Pre-fill checkout data
      setCheckoutData(prev => ({
        ...prev,
        customer_phone: response.data.customer.phone,
        customer_name: response.data.customer.name || ''
      }));
      
      // Show welcome message
      if (response.data.is_new_customer && response.data.welcome_bonus) {
        toast.success(`Welcome! You earned ${response.data.welcome_bonus} bonus points! 🎉`);
      } else {
        toast.success('Login successful!');
      }
      
      // Reset login form
      setLoginPhone('');
      setLoginOtp('');
      setOtpSent(false);
      setShowReferralInput(false);
      setSignupReferralCode('');
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error(error.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  // Logout
  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Ignore logout errors
    }
    
    localStorage.removeItem('customerToken');
    localStorage.removeItem('customerData');
    setCustomer(null);
    setIsLoggedIn(false);
    toast.success('Logged out successfully');
  };

  // Apply Coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    setCouponLoading(true);
    try {
      const response = await api.post('/coupons/apply', {
        code: couponCode,
        order_amount: cartTotal,
        customer_phone: checkoutData.customer_phone || customer?.phone
      });
      
      setAppliedCoupon(response.data);
      toast.success(`Coupon applied! You save ${formatCurrency(response.data.discount_amount)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.success('Coupon removed');
  };

  // Fetch available coupons
  const fetchAvailableCoupons = async () => {
    setLoadingCoupons(true);
    try {
      const response = await api.get('/coupons/available', {
        params: { cart_total: cartTotal }
      });
      setAvailableCoupons(response.data || []);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      setAvailableCoupons([]);
    } finally {
      setLoadingCoupons(false);
    }
  };

  // Apply coupon from available list
  const applySelectedCoupon = async (code) => {
    setCouponCode(code);
    setShowAvailableCoupons(false);
    setCouponLoading(true);
    try {
      const response = await api.post('/coupons/apply', {
        code: code,
        order_amount: cartTotal,
        customer_phone: checkoutData.customer_phone || customer?.phone
      });
      setAppliedCoupon(response.data);
      toast.success(`Coupon applied! You save ${formatCurrency(response.data.discount_amount)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const requestLocationPermission = async () => {
    if (!navigator.geolocation) {
      console.log('Geolocation not supported');
      return;
    }

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });
      
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      
      setUserLocation({ latitude: lat, longitude: lng });
      setLocationPermission('granted');
      
      // Find nearest outlet based on customer location
      try {
        const response = await api.get(`/outlets/nearest?customer_lat=${lat}&customer_lng=${lng}`);
        
        if (response.data.nearest_outlet) {
          setSelectedOutlet(response.data.nearest_outlet);
          setCheckoutData(prev => ({ ...prev, selected_outlet_id: response.data.nearest_outlet.id }));
          
          // Set delivery availability
          setDeliveryAvailable({
            available: response.data.delivery_available,
            distance_km: response.data.distance_km,
            message: response.data.message
          });
          
          // Handle different location error states
          if (response.data.location_error === 'outlets_closed_in_range') {
            setLocationError('all_outlets_closed');
            toast.warning(response.data.message, { duration: 6000 });
          } else if (response.data.location_error === 'out_of_range') {
            setLocationError('no_outlet_in_range');
            toast.info(response.data.message, { duration: 6000 });
          } else if (response.data.delivery_available) {
            setLocationError(null);
            toast.success(`Delivering from ${response.data.nearest_outlet.name}`);
          } else {
            setLocationError(null);
            toast.info(response.data.message, { duration: 5000 });
          }
        }
      } catch (error) {
        console.error('Error finding nearest outlet:', error);
        // Fallback to checking delivery with current outlet
        checkDeliveryAvailability(lat, lng);
      }
    } catch (error) {
      console.log('Location permission denied:', error);
      setLocationPermission('denied');
    }
  };

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      console.log('Notifications not supported');
      return;
    }

    if (Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  };

  const checkDeliveryAvailability = async (lat, lng) => {
    if (!selectedOutlet) return;

    try {
      const response = await api.post('/outlets/check-delivery', {
        outlet_id: selectedOutlet.id,
        customer_latitude: lat,
        customer_longitude: lng
      });
      setDeliveryAvailable(response.data);
    } catch (error) {
      console.error('Error checking delivery:', error);
    }
  };

  const fetchOutlets = async () => {
    try {
      const response = await api.get('/outlets');
      setOutlets(response.data.filter(o => o.active));
    } catch (error) {
      console.error('Error fetching outlets:', error);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const response = await api.get('/payment-settings');
      setPaymentSettings(response.data);
      // Don't auto-select payment method - user must explicitly choose
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    }
  };

  const fetchLoyaltyConfig = async () => {
    try {
      const response = await api.get('/loyalty/settings');
      setLoyaltyConfig(response.data);
    } catch (error) {
      console.error('Error fetching loyalty config:', error);
      // Use defaults if fetch fails
      setLoyaltyConfig({
        loyalty_enabled: true,
        points_per_rupee: 1,
        rupees_per_point: 0.25,
        min_redeem_points: 100,
        welcome_bonus: 50,
        referral_bonus_referrer: 100,
        referral_bonus_referee: 50,
        points_expiry_days: 180
      });
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      // Filter active categories and add 'All' at the beginning
      const activeCategories = response.data
        .filter(c => c.active !== false) // Include if active is true or undefined
        .map(c => c.name);
      setCategories(['All', ...activeCategories]);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback to default categories if API fails
      setCategories(['All', 'Burgers', 'Prawns', 'Fish Fry', 'Combos', 'Beverages']);
    }
  };

  const fetchHeroSettings = async () => {
    try {
      const response = await api.get('/site-settings');
      setHeroSettings(response.data);
      // Also update site settings for fees and contact info
      setSiteSettings({
        delivery_fee: response.data.delivery_fee || 40,
        platform_fee: response.data.platform_fee || 5,
        free_delivery_threshold: response.data.free_delivery_threshold || 500,
        contact_phone: response.data.contact_phone || '',
        contact_whatsapp: response.data.contact_whatsapp || '',
        contact_email: response.data.contact_email || '',
        operating_hours: response.data.operating_hours || '10 AM - 12 AM'
      });
    } catch (error) {
      console.error('Error fetching hero settings:', error);
      // Keep defaults
    }
  };

  const fetchSavedAddresses = async () => {
    try {
      const response = await api.get('/customers/me');
      if (response.data?.saved_addresses) {
        setSavedAddresses(response.data.saved_addresses);
        // Auto-select default address
        const defaultAddr = response.data.saved_addresses.find(a => a.is_default);
        if (defaultAddr) {
          selectSavedAddress(defaultAddr);
        }
      }
    } catch (error) {
      console.error('Error fetching saved addresses:', error);
    }
  };

  const selectSavedAddress = (address) => {
    setSelectedAddressId(address.id);
    setShowNewAddressForm(false);
    setCheckoutData(prev => ({
      ...prev,
      customer_name: address.name,
      customer_phone: address.phone,
      flat_house: address.flat_house,
      street_area: address.street_area,
      landmark: address.landmark || '',
      city: address.city,
      pincode: address.pincode || '',
      latitude: address.latitude,
      longitude: address.longitude
    }));
  };

  const handleNewAddress = () => {
    setSelectedAddressId(null);
    setShowNewAddressForm(true);
    setCheckoutData(prev => ({
      ...prev,
      customer_name: '',
      customer_phone: '+91',
      flat_house: '',
      street_area: '',
      landmark: '',
      city: '',
      pincode: '',
      latitude: null,
      longitude: null
    }));
  };

  const saveCurrentAddress = async () => {
    if (!checkoutData.flat_house || !checkoutData.street_area || !checkoutData.city) {
      toast.error('Please fill in complete address before saving');
      return;
    }
    
    const fullAddress = [
      checkoutData.flat_house,
      checkoutData.street_area,
      checkoutData.landmark,
      checkoutData.city,
      checkoutData.pincode
    ].filter(Boolean).join(', ');
    
    const newAddress = {
      label: addressLabel,
      name: checkoutData.customer_name,
      phone: checkoutData.customer_phone,
      flat_house: checkoutData.flat_house,
      street_area: checkoutData.street_area,
      landmark: checkoutData.landmark,
      city: checkoutData.city,
      pincode: checkoutData.pincode,
      full_address: fullAddress,
      latitude: checkoutData.latitude,
      longitude: checkoutData.longitude,
      is_default: savedAddresses.length === 0 // First address is default
    };
    
    try {
      await api.post('/customers/me/addresses', newAddress);
      toast.success('Address saved!');
      fetchSavedAddresses();
      setSaveThisAddress(false);
    } catch (error) {
      console.error('Error saving address:', error);
      toast.error('Failed to save address');
    }
  };

  const deleteSavedAddress = async (addressId) => {
    try {
      await api.delete(`/customers/me/addresses/${addressId}`);
      toast.success('Address deleted');
      fetchSavedAddresses();
      if (selectedAddressId === addressId) {
        handleNewAddress();
      }
    } catch (error) {
      console.error('Error deleting address:', error);
      toast.error('Failed to delete address');
    }
  };

  // ============ FAVORITES FUNCTIONS ============
  
  const fetchFavorites = async () => {
    try {
      const response = await api.get('/favorites');
      const favoriteIds = new Set(response.data.map(p => p.id));
      setFavorites(favoriteIds);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const toggleFavorite = async (productId) => {
    if (!isLoggedIn) {
      toast.error('Please login to add favorites');
      setShowLogin(true);
      return;
    }
    
    const isFavorite = favorites.has(productId);
    
    try {
      if (isFavorite) {
        await api.delete(`/favorites/${productId}`);
        setFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(productId);
          return newSet;
        });
        toast.success('Removed from favorites');
      } else {
        await api.post('/favorites', { product_id: productId });
        setFavorites(prev => new Set([...prev, productId]));
        toast.success('Added to favorites ❤️');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorites');
    }
  };

  // Fetch favorites when user logs in
  useEffect(() => {
    if (isLoggedIn) {
      fetchFavorites();
      fetchRecentOrders();
    } else {
      setFavorites(new Set());
      setRecentOrders([]);
    }
  }, [isLoggedIn]);

  // Fetch recent orders for Quick Reorder
  const fetchRecentOrders = async () => {
    if (!isLoggedIn) return;
    setLoadingRecentOrders(true);
    try {
      const response = await api.get('/customers/orders?limit=5');
      // Filter to completed/delivered orders only for reordering
      const completedOrders = response.data.filter(o => 
        ['completed', 'delivered'].includes(o.status)
      );
      setRecentOrders(completedOrders.slice(0, 3)); // Show max 3 recent orders
    } catch (error) {
      console.error('Error fetching recent orders:', error);
    } finally {
      setLoadingRecentOrders(false);
    }
  };

  // Quick Reorder handler
  const handleQuickReorder = async (order) => {
    if (!selectedOutlet) {
      toast.error('Please select an outlet first');
      return;
    }
    
    setReorderingItems(order.id);
    try {
      // Fetch current products to check availability
      const response = await api.get(`/products?outlet_id=${selectedOutlet.id}`);
      const currentProducts = response.data;
      
      let addedCount = 0;
      let unavailableItems = [];
      
      for (const item of order.items) {
        const product = currentProducts.find(p => p.id === item.product_id);
        
        if (!product || product.available === false || !product.stock || product.stock <= 0) {
          unavailableItems.push(item.product_name);
          continue;
        }
        
        // Use available stock or original quantity
        const qty = Math.min(item.quantity, product.stock);
        addToCart({
          id: product.id,
          name: product.name,
          price: product.price,
          stock: product.stock,
          packaging_fee: product.packaging_fee
        }, qty);
        addedCount++;
      }
      
      if (addedCount > 0) {
        toast.success(`Added ${addedCount} item(s) to cart!`);
      }
      
      if (unavailableItems.length > 0) {
        toast.warning(`${unavailableItems.length} item(s) unavailable: ${unavailableItems.slice(0, 2).join(', ')}${unavailableItems.length > 2 ? '...' : ''}`);
      }
      
      if (addedCount === 0) {
        toast.error('No items available for reorder');
      }
    } catch (error) {
      console.error('Error reordering:', error);
      toast.error('Failed to reorder. Please try again.');
    } finally {
      setReorderingItems(null);
    }
  };

  const openMapPicker = () => {
    // If we already have coordinates, use them as center
    if (checkoutData.latitude && checkoutData.longitude) {
      setMapCenter({ lat: checkoutData.latitude, lng: checkoutData.longitude });
    }
    setShowMapPicker(true);
  };

  const getCurrentLocation = () => {
    setGettingLocation(true);
    
    if (!navigator.geolocation) {
      setGettingLocation(false);
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    
    // Check permission status first (if supported)
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'denied') {
          setGettingLocation(false);
          toast.error(
            'Location access is blocked. Please enable it in your browser settings.',
            { duration: 5000 }
          );
          return;
        }
        // Permission is granted or will prompt - proceed with getting location
        fetchGPSLocation();
      }).catch(() => {
        // Permissions API not fully supported, try directly
        fetchGPSLocation();
      });
    } else {
      // Permissions API not available, try directly
      fetchGPSLocation();
    }
  };

  const fetchGPSLocation = () => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        // Update map center and checkout data
        setMapCenter({ lat, lng });
        setCheckoutData(prev => ({
          ...prev,
          latitude: lat,
          longitude: lng
        }));
        
        // Try to get address from coordinates using reverse geocoding
        try {
          if (window.google && window.google.maps) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
              if (status === 'OK' && results[0]) {
                const address = results[0];
                const components = address.address_components;
                
                let streetNumber = '';
                let route = '';
                let sublocality = '';
                let city = '';
                let pincode = '';
                
                components.forEach(comp => {
                  if (comp.types.includes('street_number')) streetNumber = comp.long_name;
                  if (comp.types.includes('route')) route = comp.long_name;
                  if (comp.types.includes('sublocality_level_1') || comp.types.includes('sublocality')) sublocality = comp.long_name;
                  if (comp.types.includes('locality')) city = comp.long_name;
                  if (comp.types.includes('postal_code')) pincode = comp.long_name;
                });
                
                setCheckoutData(prev => ({
                  ...prev,
                  street_area: [route, sublocality].filter(Boolean).join(', ') || sublocality || '',
                  city: city || prev.city,
                  pincode: pincode || prev.pincode
                }));
              }
            });
          }
        } catch (e) {
          console.error('Geocoding error:', e);
        }
        
        // Update the map marker position
        updateMapToCurrentLocation(lat, lng);
        
        setGettingLocation(false);
        toast.success('Location detected successfully!');
      },
      (error) => {
        setGettingLocation(false);
        console.error('Geolocation error:', error);
        
        // Provide specific error messages based on error code
        switch(error.code) {
          case error.PERMISSION_DENIED:
            toast.error(
              'Location permission denied. Please allow location access and try again.',
              { duration: 5000 }
            );
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Location information is unavailable. Please try again.');
            break;
          case error.TIMEOUT:
            toast.error('Location request timed out. Please try again.');
            break;
          default:
            toast.error('Could not get your location. Please try again.');
        }
      },
      { 
        enableHighAccuracy: true, 
        timeout: 15000,
        maximumAge: 0 
      }
    );
  };

  const confirmMapLocation = () => {
    // Location is already saved in checkoutData from getCurrentLocation or map interaction
    setShowMapPicker(false);
    if (checkoutData.latitude && checkoutData.longitude) {
      toast.success('Location confirmed!');
    }
  };

  // Initialize interactive Google Map with draggable marker
  const initializeMap = () => {
    if (!window.google || !window.google.maps || !mapContainerRef.current) {
      return;
    }

    // Get initial position
    const initialLat = checkoutData.latitude || mapCenter.lat;
    const initialLng = checkoutData.longitude || mapCenter.lng;
    const initialPosition = { lat: initialLat, lng: initialLng };

    // Create map
    const map = new window.google.maps.Map(mapContainerRef.current, {
      center: initialPosition,
      zoom: checkoutData.latitude ? 17 : 12,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: [
        { elementType: "geometry", stylers: [{ color: "#1d1d1d" }] },
        { elementType: "labels.text.stroke", stylers: [{ color: "#1d1d1d" }] },
        { elementType: "labels.text.fill", stylers: [{ color: "#8e8e8e" }] },
        { featureType: "road", elementType: "geometry", stylers: [{ color: "#383838" }] },
        { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e3d5e" }] },
      ]
    });
    mapInstanceRef.current = map;

    // Create draggable marker
    const marker = new window.google.maps.Marker({
      position: initialPosition,
      map: map,
      draggable: true,
      animation: window.google.maps.Animation.DROP,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: '#FF4500',
        fillOpacity: 1,
        strokeColor: '#FFFFFF',
        strokeWeight: 3,
      }
    });
    markerRef.current = marker;

    // Update coordinates when marker is dragged
    marker.addListener('dragend', () => {
      const position = marker.getPosition();
      const lat = position.lat();
      const lng = position.lng();
      
      setCheckoutData(prev => ({
        ...prev,
        latitude: lat,
        longitude: lng
      }));

      // Try reverse geocoding to get address
      reverseGeocodePosition(lat, lng);
    });

    // Also allow clicking on map to move marker
    map.addListener('click', (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      
      marker.setPosition(e.latLng);
      
      setCheckoutData(prev => ({
        ...prev,
        latitude: lat,
        longitude: lng
      }));

      reverseGeocodePosition(lat, lng);
    });
  };

  // Reverse geocode to get address from coordinates
  const reverseGeocodePosition = (lat, lng) => {
    if (!window.google || !window.google.maps) return;
    
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const components = results[0].address_components;
        
        let route = '';
        let sublocality = '';
        let city = '';
        let pincode = '';
        
        components.forEach(comp => {
          if (comp.types.includes('route')) route = comp.long_name;
          if (comp.types.includes('sublocality_level_1') || comp.types.includes('sublocality')) sublocality = comp.long_name;
          if (comp.types.includes('locality')) city = comp.long_name;
          if (comp.types.includes('postal_code')) pincode = comp.long_name;
        });
        
        setCheckoutData(prev => ({
          ...prev,
          street_area: [route, sublocality].filter(Boolean).join(', ') || prev.street_area,
          city: city || prev.city,
          pincode: pincode || prev.pincode
        }));
      }
    });
  };

  // Update map when getting current location
  const updateMapToCurrentLocation = (lat, lng) => {
    if (mapInstanceRef.current && markerRef.current) {
      const position = { lat, lng };
      mapInstanceRef.current.setCenter(position);
      mapInstanceRef.current.setZoom(17);
      markerRef.current.setPosition(position);
    }
  };

  // Effect to initialize map when dialog opens
  useEffect(() => {
    if (showMapPicker && window.google && window.google.maps) {
      // Small delay to ensure dialog is rendered
      setTimeout(() => {
        initializeMap();
      }, 100);
    }
  }, [showMapPicker]);

  const handleMapLocationSelect = (lat, lng, address) => {
    setCheckoutData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng,
      street_area: address || prev.street_area
    }));
    setShowMapPicker(false);
    toast.success('Location pinned successfully!');
  };

  const fetchProducts = async (outletIdToUse = null) => {
    try {
      const oid = outletIdToUse || selectedOutlet?.id || outletId || 'main';
      const response = await api.get(`/products?outlet_id=${oid}`);
      setProducts(response.data);
      
      // Also fetch add-on only products for cross-sell
      try {
        const addonResponse = await api.get('/products/addon-only');
        setAddonProducts(addonResponse.data);
      } catch (e) {
        console.error('Error fetching add-on products:', e);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load menu');
    }
  };

  const addToCart = (product, addons = [], customizations = {}, notes = '') => {
    const availableStock = product.stock || 0;
    
    // Calculate addon total
    const addonTotal = addons.reduce((sum, addon) => sum + (addon.price || 0), 0);
    
    // Calculate customization total
    const customizationTotal = Object.values(customizations).reduce((sum, opt) => sum + (opt.price || 0), 0);
    
    // Total price including base + addons + customizations
    const totalItemPrice = product.price + addonTotal + customizationTotal;
    
    // Create unique key for cart item (product + addons + customizations)
    const addonKey = addons.map(a => a.name).sort().join(',');
    const customKey = Object.entries(customizations).map(([g, o]) => `${g}:${o.name}`).sort().join(',');
    const cartItemKey = `${product.id}-${addonKey}-${customKey}`;
    
    const existingItem = cart.find(item => item.cart_key === cartItemKey);
    const currentQtyInCart = existingItem ? existingItem.quantity : 0;
    
    // Check if adding one more would exceed available stock
    if (currentQtyInCart >= availableStock) {
      toast.error(`Only ${availableStock} available in stock`);
      return;
    }
    
    if (existingItem) {
      setCart(cart.map(item => 
        item.cart_key === cartItemKey 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        cart_key: cartItemKey,
        product_id: product.id,
        product_name: product.name,
        price: totalItemPrice,
        base_price: product.price,
        quantity: 1,
        max_stock: availableStock,
        selected_addons: addons,
        selected_customizations: Object.entries(customizations).map(([group, opt]) => ({
          group,
          option: opt.name,
          price: opt.price || 0
        })),
        item_notes: notes || null
      }]);
    }
    
    // Show success with addon info
    if (addons.length > 0 || Object.keys(customizations).length > 0) {
      const extras = [...addons.map(a => a.name), ...Object.values(customizations).map(c => c.name)].join(', ');
      toast.success(`${product.name} (${extras}) added to cart`);
    } else {
      toast.success(`${product.name} added to cart`);
    }
  };

  // Add add-on products to cart (separate line items)
  const addAddonProductsToCart = (addonProductsList) => {
    addonProductsList.forEach(product => {
      const cartItemKey = `${product.id}-addon-product`;
      const existingItem = cart.find(item => item.cart_key === cartItemKey);
      
      if (existingItem) {
        // Update quantity
        setCart(cart.map(item => 
          item.cart_key === cartItemKey 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        ));
      } else {
        // Add new item
        setCart(prev => [...prev, {
          cart_key: cartItemKey,
          product_id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          quantity: 1,
          max_stock: product.stock || 100,
          addons: [],
          customizations: [],
          item_notes: null,
          is_addon_product: true
        }]);
      }
    });
    
    if (addonProductsList.length > 0) {
      toast.success(`${addonProductsList.map(p => p.name).join(', ')} added to cart`);
    }
  };

  const updateQuantity = (cartKey, delta) => {
    setCart(cart.map(item => {
      if (item.cart_key === cartKey || item.product_id === cartKey) {
        const newQuantity = item.quantity + delta;
        
        // If increasing, check stock limit
        if (delta > 0) {
          const product = products.find(p => p.id === item.product_id);
          const availableStock = product?.stock || item.max_stock || 999;
          if (newQuantity > availableStock) {
            toast.error(`Only ${availableStock} available in stock`);
            return item; // Return unchanged
          }
        }
        
        return newQuantity > 0 ? { ...item, quantity: newQuantity } : null;
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (cartKey) => {
    setCart(cart.filter(item => item.cart_key !== cartKey && item.product_id !== cartKey));
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  // Calculate GST from cart items
  const calculateGST = () => {
    return cart.reduce((sum, item) => {
      // Find the product to check tax settings
      const product = products.find(p => p.id === item.product_id);
      if (product?.tax_enabled && product?.tax_percentage > 0) {
        const itemTotal = item.price * item.quantity;
        return sum + (itemTotal * product.tax_percentage / 100);
      }
      return sum;
    }, 0);
  };

  // Calculate delivery fee - now based on distance
  const [calculatedDeliveryFee, setCalculatedDeliveryFee] = useState(0);
  const [deliveryFeeInfo, setDeliveryFeeInfo] = useState(null);
  
  // Function to calculate distance between two coordinates using Haversine formula
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };
  
  // Fetch delivery fee when location changes
  useEffect(() => {
    const fetchDeliveryFee = async () => {
      if (checkoutData.order_type !== 'delivery' || !checkoutData.latitude || !checkoutData.longitude) {
        setCalculatedDeliveryFee(0);
        setDeliveryFeeInfo(null);
        return;
      }
      
      // Get outlet location
      const currentOutletId = checkoutData.selected_outlet_id || outletId || 'main';
      try {
        const outletRes = await api.get(`/outlets/${currentOutletId}`);
        const outlet = outletRes.data;
        
        if (!outlet.latitude || !outlet.longitude) {
          // No outlet GPS, use default fee
          setCalculatedDeliveryFee(siteSettings.delivery_fee);
          setDeliveryFeeInfo({ type: 'default', message: 'Default delivery fee' });
          return;
        }
        
        // Calculate distance
        const distance = calculateDistance(
          parseFloat(outlet.latitude), parseFloat(outlet.longitude),
          parseFloat(checkoutData.latitude), parseFloat(checkoutData.longitude)
        );
        
        // Get fee from API
        const feeRes = await api.get(`/delivery-fee/calculate?outlet_id=${currentOutletId}&distance_km=${distance.toFixed(2)}`);
        
        if (feeRes.data.fee === null) {
          // Out of delivery range
          setDeliveryFeeInfo({ 
            type: 'out_of_range', 
            message: feeRes.data.message,
            distance: distance.toFixed(1)
          });
          setCalculatedDeliveryFee(0);
        } else {
          setCalculatedDeliveryFee(feeRes.data.fee);
          setDeliveryFeeInfo({ 
            type: feeRes.data.range_type,
            range: feeRes.data.range,
            distance: distance.toFixed(1)
          });
        }
      } catch (error) {
        console.error('Error calculating delivery fee:', error);
        // Fallback to default
        setCalculatedDeliveryFee(siteSettings.delivery_fee);
        setDeliveryFeeInfo({ type: 'default', message: 'Default delivery fee' });
      }
    };
    
    fetchDeliveryFee();
  }, [checkoutData.latitude, checkoutData.longitude, checkoutData.order_type, checkoutData.selected_outlet_id, outletId, siteSettings.delivery_fee]);

  // Calculate delivery fee (use calculated if available, otherwise static)
  const calculateDeliveryFee = () => {
    if (checkoutData.order_type !== 'delivery') return 0;
    
    // Check for free delivery threshold
    const itemTotal = calculateTotal();
    if (itemTotal >= siteSettings.free_delivery_threshold) return 0;
    
    return calculatedDeliveryFee || siteSettings.delivery_fee;
  };

  // Calculate packaging & handling fee (sum of each product's packaging fee × quantity)
  // No packaging fee for dine in orders
  const calculatePackagingFee = () => {
    if (checkoutData.order_type === 'dine_in') return 0;
    return cart.reduce((total, item) => {
      const product = products.find(p => p.id === item.product_id);
      const itemPackagingFee = product?.packaging_fee || 0;
      return total + (itemPackagingFee * item.quantity);
    }, 0);
  };

  // Calculate cart total once
  const cartTotal = calculateTotal();
  const gstAmount = calculateGST();
  const deliveryFee = calculateDeliveryFee();
  const packagingFee = calculatePackagingFee();
  
  // Grand total including all fees
  const grandTotal = cartTotal + gstAmount + deliveryFee + packagingFee - (appliedCoupon?.discount_amount || 0) - pointsDiscount;

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    
    if (!checkoutData.customer_name || !checkoutData.customer_name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    if (!checkoutData.customer_phone || checkoutData.customer_phone.length < 13) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    // Validate payment method is selected
    if (!checkoutData.payment_method) {
      toast.error('Please select a payment method');
      return;
    }
    
    // Validate payment method is enabled
    if (checkoutData.payment_method === 'cod' && !paymentSettings.cod_enabled) {
      toast.error('Cash on Delivery is not available. Please select another payment method.');
      return;
    }
    
    // Build full address from components for delivery orders
    let fullAddress = '';
    if (checkoutData.order_type === 'delivery') {
      if (!checkoutData.flat_house || !checkoutData.street_area || !checkoutData.city) {
        toast.error('Please enter complete delivery address');
        return;
      }
      const addressParts = [
        checkoutData.flat_house,
        checkoutData.street_area,
        checkoutData.landmark,
        checkoutData.city,
        checkoutData.pincode
      ].filter(Boolean);
      fullAddress = addressParts.join(', ');
    }
    
    setLoading(true);
    setLoadingMessage('Checking availability...');
    
    // Show slow network warning
    if (networkStatus === 'slow') {
      toast.info('Slow connection detected. Order may take longer to process...', { duration: 4000 });
    }
    
    try {
      // ============ VALIDATE CHECKOUT BEFORE PROCEEDING ============
      // Check outlet status, delivery range, and stock availability
      // For dine-in/pickup, also pass customer coordinates for proximity check
      setLoadingMessage('Validating order details...');
      const validationResponse = await api.post('/validate-checkout', {
        outlet_id: checkoutData.selected_outlet_id || outletId,
        customer_latitude: userLocation?.latitude || (checkoutData.order_type === 'delivery' ? parseFloat(checkoutData.latitude) : null),
        customer_longitude: userLocation?.longitude || (checkoutData.order_type === 'delivery' ? parseFloat(checkoutData.longitude) : null),
        order_type: checkoutData.order_type,
        items: cart.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity
        }))
      });
      
      const validation = validationResponse.data;
      
      // Handle validation errors
      if (!validation.valid) {
        setLoading(false);
        
        for (const error of validation.errors) {
          if (error.type === 'outlet_closed') {
            toast.error(error.message, { duration: 5000 });
            // Refresh outlet status
            pollOutletStatus();
            return;
          }
          
          if (error.type === 'out_of_range') {
            toast.error(error.message, { duration: 6000 });
            return;
          }
          
          if (error.type === 'too_far_for_dinein') {
            toast.error(error.message, { duration: 6000 });
            return;
          }
          
          if (error.type === 'out_of_stock') {
            // Remove out of stock items from cart and show message
            const outOfStockIds = error.items.map(i => i.product_id);
            const itemNames = error.items.map(i => 
              i.available === 0 
                ? `${i.product_name} (sold out)` 
                : `${i.product_name} (only ${i.available} available)`
            ).join(', ');
            
            toast.error(`Some items are no longer available: ${itemNames}. Please update your cart.`, { duration: 6000 });
            
            // Update cart quantities or remove items
            setCart(prev => prev.map(item => {
              const stockInfo = error.items.find(i => i.product_id === item.product_id);
              if (stockInfo) {
                if (stockInfo.available === 0) {
                  return null; // Remove item
                }
                return { ...item, quantity: stockInfo.available }; // Reduce to available
              }
              return item;
            }).filter(Boolean));
            
            return;
          }
          
          // Handle any other error types with their message
          if (error.message) {
            toast.error(error.message, { duration: 5000 });
            return;
          }
        }
        
        toast.error('Unable to proceed with checkout. Please try again.');
        return;
      }
      
      // Show warnings but allow to proceed
      for (const warning of validation.warnings || []) {
        if (warning.type === 'low_stock') {
          toast.warning(`Limited stock: ${warning.items.map(i => i.product_name).join(', ')}`, { duration: 4000 });
        }
      }
      // ============ END VALIDATION ============
      
      // Calculate final amount with coupon, points, and fees
      const itemTotal = cartTotal;
      const gst = gstAmount;
      const delivery = deliveryFee;
      const packaging = packagingFee;
      const couponDiscount = appliedCoupon ? appliedCoupon.discount_amount : 0;
      const totalDiscount = couponDiscount + pointsDiscount;
      const finalAmount = Math.max(0, itemTotal + gst + delivery + packaging - totalDiscount);
      
      const orderData = {
        items: cart,
        total_amount: finalAmount,
        subtotal: itemTotal,
        gst_amount: gst,
        delivery_fee: delivery,
        packaging_fee: packaging,
        order_type: checkoutData.order_type,
        payment_method: checkoutData.payment_method,
        customer_name: checkoutData.customer_name,
        customer_phone: checkoutData.customer_phone,
        customer_address: checkoutData.order_type === 'delivery' ? fullAddress : null,
        delivery_latitude: checkoutData.order_type === 'delivery' ? checkoutData.latitude : null,
        delivery_longitude: checkoutData.order_type === 'delivery' ? checkoutData.longitude : null,
        outlet_id: checkoutData.selected_outlet_id || outletId,
        coupon_code: appliedCoupon?.code || null,
        discount_amount: couponDiscount,
        loyalty_points_used: pointsToRedeem,
        loyalty_points_discount: pointsDiscount
      };
      
      // Process payment based on method
      if (checkoutData.payment_method === 'cod') {
        // Cash on Delivery - create order directly
        setLoadingMessage('Placing your order...');
        
        // Check if offline - save for later sync
        if (networkStatus === 'offline') {
          saveOrderOffline(orderData);
          return;
        }
        
        try {
          const orderResponse = await api.post('/orders', orderData);
          const orderId = orderResponse.data.id;
          toast.success('Order placed successfully! Pay on delivery. 🎉');
          setLoadingMessage('');
          handleOrderSuccess(orderId, 0);
        } catch (orderError) {
          // If network failed during order, offer to save offline
          if (orderError.code === 'ECONNABORTED' || !navigator.onLine) {
            toast.info('Network unavailable. Saving order for later...', { duration: 3000 });
            saveOrderOffline(orderData);
            return;
          }
          throw orderError;
        }
      } else {
        // Online payment (UPI, Card, Net Banking) via Razorpay
        // Can't process online payments offline
        if (networkStatus === 'offline') {
          toast.error('Online payment requires internet connection. Please use Cash on Delivery or try again when connected.');
          setLoading(false);
          setLoadingMessage('');
          return;
        }
        
        // Don't create order yet - only after payment succeeds
        setLoadingMessage('Connecting to payment gateway...');
        
        // Use retry logic for payment gateway connection
        try {
          await retryPaymentWithBackoff(orderData, finalAmount, paymentRetryCount);
        } catch (paymentError) {
          if (paymentRetryCount >= MAX_PAYMENT_RETRIES) {
            toast.error('Payment gateway connection failed. Please try again later or use Cash on Delivery.');
          }
          throw paymentError;
        }
      }
    } catch (error) {
      console.error('Checkout error:', error);
      // Show more descriptive error messages
      const errorDetail = error.response?.data?.detail;
      const errorMsg = error.response?.data?.message;
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Request timed out. Your connection may be slow. Please try again.');
      } else if (errorDetail) {
        toast.error(errorDetail);
      } else if (errorMsg) {
        toast.error(errorMsg);
      } else if (error.message) {
        toast.error(`Order failed: ${error.message}`);
      } else {
        toast.error('Failed to place order. Please try again.');
      }
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const initiateRazorpayPayment = async (orderData, amount) => {
    try {
      // Check if Razorpay SDK is loaded
      if (!window.Razorpay) {
        toast.error('Payment service is loading. Please try again in a moment.');
        setLoading(false);
        return;
      }
      
      // Create Razorpay order (payment intent only, no order in DB yet)
      const razorpayOrderResponse = await api.post('/payment/create-order', { amount });
      
      if (!razorpayOrderResponse.data || !razorpayOrderResponse.data.order_id) {
        throw new Error('Invalid payment order response');
      }
      
      const razorpayOrderId = razorpayOrderResponse.data.order_id;
      const keyId = razorpayOrderResponse.data.key_id;
      
      // Store pending payment data for webhook processing
      // This ensures order can be created even if customer closes browser after payment
      try {
        await api.post('/payment/store-pending', {
          razorpay_order_id: razorpayOrderId,
          order_data: {
            ...orderData,
            id: `order_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            payment_status: 'pending',
            total_amount: amount
          }
        });
      } catch (e) {
        console.warn('Failed to store pending payment data:', e);
        // Continue anyway - webhook is a backup mechanism
      }
      
      // Close checkout dialog before opening Razorpay to prevent overlay conflicts
      setShowCheckout(false);
      setLoading(false);
      setLoadingMessage('');
      
      // Detect if on mobile for UPI Intent flow
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // Configure Razorpay options with UPI Intent for mobile
      const options = {
        key: keyId,
        amount: razorpayOrderResponse.data.amount,
        currency: razorpayOrderResponse.data.currency,
        name: 'Fish on Fire',
        description: 'Food Order Payment',
        order_id: razorpayOrderId,
        prefill: {
          name: checkoutData.customer_name,
          contact: checkoutData.customer_phone,
        },
        theme: {
          color: '#FF4500'
        },
        // Enable UPI Intent flow on mobile - auto opens GPay/PhonePe/Paytm
        config: {
          display: {
            blocks: {
              // Prioritize UPI options on mobile
              utib: {
                name: "Pay via UPI",
                instruments: [
                  {
                    method: "upi",
                    flows: isMobile ? ["intent", "collect", "qr"] : ["collect", "qr"],
                    apps: ["google_pay", "phonepe", "paytm"]
                  }
                ]
              },
              other: {
                name: "Other Payment Methods",
                instruments: [
                  { method: "card" },
                  { method: "netbanking" },
                  { method: "wallet" }
                ]
              }
            },
            sequence: ["block.utib", "block.other"],
            preferences: {
              show_default_blocks: false
            }
          }
        },
        // UPI specific options
        method: {
          upi: {
            flow: isMobile ? 'intent' : 'collect' // 'intent' opens apps directly on mobile
          }
        },
        handler: async function (response) {
          // Payment successful - NOW create the order and verify payment
          try {
            setLoading(true);
            // Create the order in database
            const orderResponse = await api.post('/orders', {
              ...orderData,
              payment_status: 'paid',
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id
            });
            const orderId = orderResponse.data.id;
            
            // Verify payment on backend
            const verifyResponse = await api.post('/payment/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order_id: orderId
            });
            
            const pointsEarned = verifyResponse.data?.points_earned || 0;
            toast.success(`Payment successful! ${pointsEarned > 0 ? `You earned ${pointsEarned} points!` : ''} 🎉`);
            handleOrderSuccess(orderId, pointsEarned);
          } catch (error) {
            console.error('Order creation/payment verification failed:', error);
            toast.error('Payment received but order creation failed. Please contact support with your payment ID.');
            setLoading(false);
          }
        },
        modal: {
          ondismiss: function() {
            // Payment was cancelled - reopen checkout
            toast.info('Payment cancelled. You can try again.');
            setShowCheckout(true);
          },
          escape: true,
          backdropclose: false
        }
      };
      
      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        // Payment failed - reopen checkout
        console.error('Payment failed:', response.error);
        toast.error(`Payment failed: ${response.error.description}. Please try again.`);
        setShowCheckout(true);
      });
      razorpay.open();
      
    } catch (error) {
      console.error('Razorpay order creation failed:', error);
      toast.error('Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  const handleOrderSuccess = async (orderId, pointsEarned) => {
    // Save customer name to profile if logged in and name was provided
    if (isLoggedIn && checkoutData.customer_name && customer) {
      try {
        // Update profile name if different from current
        if (customer.name !== checkoutData.customer_name) {
          await api.put('/customers/me', { name: checkoutData.customer_name });
          // Update local customer state
          setCustomer(prev => prev ? {...prev, name: checkoutData.customer_name} : null);
        }
      } catch (e) {
        console.log('Could not update profile name:', e);
      }
    }
    
    setCart([]);
    localStorage.removeItem('fishOnFireCart'); // Clear persisted cart
    setShowCheckout(false);
    setShowCart(false);
    setAppliedCoupon(null);
    setCouponCode('');
    setPointsToRedeem(0);
    setPointsDiscount(0);
    setLoading(false);
    setPaymentRetryCount(0); // Reset retry count
    
    // Redirect to tracking page
    window.location.href = `/track/${orderId}`;
  };

  // ============ OFFLINE ORDER QUEUE FUNCTIONS ============
  
  // Save order to offline queue
  const saveOrderOffline = (orderData) => {
    const offlineOrder = {
      ...orderData,
      id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      created_at: new Date().toISOString(),
      synced: false
    };
    
    const updatedOrders = [...offlineOrders, offlineOrder];
    setOfflineOrders(updatedOrders);
    localStorage.setItem('offlineOrders', JSON.stringify(updatedOrders));
    
    toast.success('Order saved offline. It will be placed when you reconnect.', { duration: 5000 });
    
    // Clear cart
    setCart([]);
    localStorage.removeItem('fishOnFireCart');
    setShowCheckout(false);
    setShowCart(false);
    setLoading(false);
  };
  
  // Sync offline orders when back online
  const syncOfflineOrders = async () => {
    if (offlineOrders.length === 0 || networkStatus === 'offline') return;
    
    setSyncingOrders(true);
    let successCount = 0;
    let failedOrders = [];
    
    for (const order of offlineOrders) {
      if (order.synced) continue;
      
      try {
        // Only COD orders can be synced (online payments require real-time)
        if (order.payment_method !== 'cod') {
          failedOrders.push({ ...order, error: 'Online payment orders cannot be synced offline' });
          continue;
        }
        
        const { id, created_at, synced, ...orderData } = order;
        const response = await api.post('/orders', orderData);
        
        successCount++;
        toast.success(`Offline order synced! Order #${response.data.order_number || response.data.id.slice(0, 8)}`);
      } catch (error) {
        console.error('Failed to sync offline order:', error);
        failedOrders.push({ ...order, error: error.message });
      }
    }
    
    // Update offline orders - keep only failed ones
    setOfflineOrders(failedOrders);
    localStorage.setItem('offlineOrders', JSON.stringify(failedOrders));
    
    setSyncingOrders(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} offline order(s) synced successfully!`);
    }
    if (failedOrders.length > 0) {
      toast.error(`${failedOrders.length} order(s) failed to sync. Please try again.`);
    }
  };
  
  // Remove an offline order
  const removeOfflineOrder = (orderId) => {
    const updatedOrders = offlineOrders.filter(o => o.id !== orderId);
    setOfflineOrders(updatedOrders);
    localStorage.setItem('offlineOrders', JSON.stringify(updatedOrders));
    toast.info('Offline order removed');
  };
  
  // Auto-sync when coming back online
  useEffect(() => {
    if (networkStatus === 'good' && offlineOrders.length > 0) {
      // Delay sync slightly to ensure connection is stable
      const timer = setTimeout(() => {
        syncOfflineOrders();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [networkStatus]);

  // ============ PAYMENT RETRY WITH EXPONENTIAL BACKOFF ============
  
  const retryPaymentWithBackoff = async (orderData, amount, retryCount = 0) => {
    const delay = Math.min(1000 * Math.pow(2, retryCount), 10000); // Max 10 seconds
    
    if (retryCount > 0) {
      setLoadingMessage(`Retrying payment (${retryCount}/${MAX_PAYMENT_RETRIES})...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    
    try {
      await initiateRazorpayPayment(orderData, amount);
      setPaymentRetryCount(0);
    } catch (error) {
      if (retryCount < MAX_PAYMENT_RETRIES && error.code === 'ECONNABORTED') {
        setPaymentRetryCount(retryCount + 1);
        toast.info(`Connection timeout. Retrying in ${delay/1000}s...`);
        return retryPaymentWithBackoff(orderData, amount, retryCount + 1);
      }
      throw error;
    }
  };

  // Filter products - only show available products to customers
  const availableProducts = products.filter(p => p.available !== false);
  const filteredProducts = selectedCategory === 'All' 
    ? availableProducts 
    : selectedCategory === 'Favorites'
      ? availableProducts.filter(p => favorites.has(p.id))
      : availableProducts.filter(p => p.category === selectedCategory);
  
  // Include Favorites in categories if logged in and has favorites
  const displayCategories = isLoggedIn && favorites.size > 0 
    ? ['All', 'Favorites', ...categories.filter(c => c !== 'All')]
    : categories;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Offline Warning Banner - Only shows when truly offline */}
      {showNetworkWarning && networkStatus === 'offline' && (
        <div className="fixed top-0 left-0 right-0 z-[100] px-4 py-2 text-center text-sm font-medium bg-red-600 text-white">
          <span>📵 No internet connection. Please check your network.</span>
          <button 
            onClick={() => setShowNetworkWarning(false)}
            className="ml-3 text-xs underline"
          >
            Dismiss
          </button>
        </div>
      )}
      
      {/* Loading Overlay with Progress Messages */}
      {loading && loadingMessage && (
        <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center">
          <div className="bg-[#1a1a1a] rounded-xl p-6 text-center max-w-xs mx-4">
            <div className="w-12 h-12 border-4 border-[#FF4500] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-white font-medium">{loadingMessage}</p>
            {paymentRetryCount > 0 && (
              <p className="text-blue-400 text-sm mt-2">Retry {paymentRetryCount}/{MAX_PAYMENT_RETRIES}...</p>
            )}
          </div>
        </div>
      )}
      
      {/* Offline Orders Banner */}
      {offlineOrders.length > 0 && networkStatus !== 'offline' && (
        <div className="fixed top-0 left-0 right-0 z-[99] px-4 py-2 bg-blue-600 text-white text-center text-sm font-medium flex items-center justify-center gap-2">
          <span>📦 {offlineOrders.length} offline order(s) ready to sync</span>
          <Button 
            size="sm" 
            variant="secondary"
            onClick={syncOfflineOrders}
            disabled={syncingOrders}
            className="h-6 px-2 text-xs"
          >
            {syncingOrders ? 'Syncing...' : 'Sync Now'}
          </Button>
          <button 
            onClick={() => setShowOfflineOrders(true)}
            className="text-xs underline ml-2"
          >
            View
          </button>
        </div>
      )}
      
      {/* Header */}
      <header className={`sticky ${(showNetworkWarning && networkStatus === 'offline') ? 'top-8' : 'top-0'} z-50 glass border-b border-white/10 transition-all`}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/preview" className="flex items-center hover:opacity-80 transition-opacity cursor-pointer" data-testid="logo-link">
              <img 
                src="/logo.png" 
                alt="Fish on Fire" 
                className="h-10 sm:h-12 w-auto object-contain"
              />
            </Link>
            
            <div className="flex items-center gap-2">
              {/* User Menu */}
              {isLoggedIn ? (
                <div className="flex items-center gap-2">
                  <Link to="/profile">
                    <Button 
                      variant="ghost" 
                      className="text-white hover:bg-white/5"
                      data-testid="profile-btn"
                    >
                      <User className="w-5 h-5 mr-2" />
                      <span className="hidden sm:inline">Profile</span>
                    </Button>
                  </Link>
                  <Link to="/orders">
                    <Button 
                      variant="ghost" 
                      className="text-white hover:bg-white/5"
                      data-testid="my-orders-btn"
                    >
                      <Package className="w-5 h-5 mr-2" />
                      <span className="hidden sm:inline">Orders</span>
                    </Button>
                  </Link>
                </div>
              ) : (
                <Button 
                  onClick={() => setShowLogin(true)}
                  variant="outline"
                  className="border-[#FF4500]/50 text-[#FF4500] hover:bg-[#FF4500]/10"
                  data-testid="login-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  Login
                </Button>
              )}
              
              {/* Cart Button */}
              <button 
                onClick={() => setShowCart(true)}
                className="relative p-2 hover:bg-white/5 rounded-lg transition-colors"
                data-testid="cart-button"
              >
                <ShoppingCart className="w-6 h-6 text-white" strokeWidth={1.5} />
                {cart.length > 0 && (
                  <Badge className="absolute -top-1 -right-1 bg-[#FF4500] text-white border-0 w-5 h-5 flex items-center justify-center p-0 text-xs">
                    {cart.length}
                  </Badge>
                )}
              </button>
              
              {/* Install App Button - Only show if not installed and prompt available */}
              {!isInstalled && installPrompt && (
                <Button
                  onClick={handleInstallClick}
                  size="sm"
                  className="bg-[#FF4500] hover:bg-[#FF5722] text-white hidden sm:flex"
                  data-testid="install-app-btn"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Install
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Delivery Availability Banner */}
        {selectedOutlet && (
          <div className={`border-t border-white/10 ${
            deliveryAvailable?.available 
              ? 'bg-green-500/10' 
              : deliveryAvailable?.available === false 
                ? 'bg-orange-500/10' 
                : 'bg-blue-500/10'
          }`}>
            <div className="container mx-auto px-4 py-2">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <MapPin className={`w-4 h-4 ${
                    deliveryAvailable?.available 
                      ? 'text-green-400' 
                      : deliveryAvailable?.available === false 
                        ? 'text-orange-400' 
                        : 'text-blue-400'
                  }`} />
                  <span className={
                    deliveryAvailable?.available 
                      ? 'text-green-400' 
                      : deliveryAvailable?.available === false 
                        ? 'text-orange-400' 
                        : 'text-blue-400'
                  }>
                    {deliveryAvailable?.available 
                      ? `✓ Delivering from ${selectedOutlet.name}` 
                      : deliveryAvailable?.available === false 
                        ? `📍 Browsing ${selectedOutlet.name} (${deliveryAvailable.distance_km}km away - delivery not available)`
                        : `📍 Browsing ${selectedOutlet.name}`
                    }
                  </span>
                </div>
                {deliveryAvailable?.available && selectedOutlet.delivery_radius_km && (
                  <span className="text-gray-400 text-xs hidden sm:block">
                    {deliveryAvailable.distance_km}km away
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Location Permission Prompt - Show when no location yet */}
        {locationPermission !== 'granted' && !userLocation && (
          <div className="border-t border-white/10 bg-yellow-500/10 cursor-pointer hover:bg-yellow-500/20 transition-colors"
               onClick={requestLocationPermission}>
            <div className="container mx-auto px-4 py-2">
              <div className="flex items-center justify-between">
                <p className="text-yellow-400 text-sm">
                  📍 Enable location to find your nearest shop & check delivery
                </p>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="border-yellow-400 text-yellow-400 hover:bg-yellow-500/20 text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    requestLocationPermission();
                  }}
                >
                  Enable
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-12 md:py-20">
        {/* Hero Background Image */}
        {heroSettings.hero_image && (
          <div className="absolute inset-0">
            <img 
              src={getImageUrl(heroSettings.hero_image)} 
              alt="Hero background"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/60" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF4500]/10 via-transparent to-[#00E5FF]/10" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-5xl md:text-7xl font-bold text-white mb-4" style={{ fontFamily: 'Bebas Neue' }}>
              {heroSettings.hero_headline}
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              {heroSettings.hero_tagline}
            </p>
          </div>
        </div>
      </section>

      {/* Quick Reorder Section - Only show for logged in users with recent orders */}
      {isLoggedIn && recentOrders.length > 0 && (
        <section className="container mx-auto px-4 py-6" data-testid="quick-reorder-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Bebas Neue' }}>
              <RotateCcw className="w-5 h-5 text-[#FF4500]" />
              Order Again
            </h3>
            <Link to="/orders" className="text-[#00E5FF] text-sm hover:underline">
              View All Orders →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {recentOrders.map(order => (
              <Card 
                key={order.id} 
                className="bg-[#121212] border-white/10 hover:border-[#FF4500]/50 transition-all"
                data-testid={`quick-reorder-${order.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="text-gray-400 text-xs">Order #{order.order_number}</p>
                      <p className="text-white font-medium">
                        {formatCurrency(order.total || order.total_amount)}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs border-green-500/30 text-green-400">
                      {order.items?.length || 0} items
                    </Badge>
                  </div>
                  <p className="text-gray-500 text-xs mb-3 truncate">
                    {order.items?.map(i => i.product_name).join(', ')}
                  </p>
                  <Button
                    size="sm"
                    onClick={() => handleQuickReorder(order)}
                    disabled={reorderingItems === order.id}
                    className="w-full bg-[#FF4500] hover:bg-[#FF5722] text-white"
                    data-testid={`reorder-btn-${order.id}`}
                  >
                    {reorderingItems === order.id ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <RotateCcw className="w-4 h-4 mr-2" />
                    )}
                    Reorder
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Category Tabs */}
      <div className="container mx-auto px-4 mb-8">
        {/* Not Accepting Orders Banner */}
        {!outletStatus.isAcceptingOrders && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 mb-6 text-center">
            <p className="text-red-400 font-bold text-lg mb-1">
              🚫 We&apos;re not accepting orders right now
            </p>
            <p className="text-red-400/80 text-sm">
              Ordering hours: {outletStatus.orderingStartTime} - {outletStatus.orderingEndTime}
            </p>
            <p className="text-gray-400 text-xs mt-2">
              Please check back during our operating hours
            </p>
          </div>
        )}

        {/* Location Error Banner - Out of Range or All Outlets Closed */}
        {locationError && outletStatus.isAcceptingOrders && (
          <div className={`rounded-xl p-4 mb-6 text-center ${
            locationError === 'all_outlets_closed' 
              ? 'bg-orange-500/20 border border-orange-500/50' 
              : 'bg-yellow-500/20 border border-yellow-500/50'
          }`}>
            {locationError === 'all_outlets_closed' ? (
              <>
                <p className="text-orange-400 font-bold text-lg mb-1">
                  ⏸️ All outlets in your area are currently closed
                </p>
                <p className="text-orange-400/80 text-sm">
                  You can browse our menu, but ordering is temporarily unavailable
                </p>
              </>
            ) : (
              <>
                <p className="text-yellow-400 font-bold text-lg mb-1">
                  📍 We don&apos;t deliver to your location yet
                </p>
                <p className="text-yellow-400/80 text-sm">
                  {deliveryAvailable?.message || 'You can browse our menu, but delivery is not available at your address'}
                </p>
                <p className="text-gray-400 text-xs mt-2">
                  Try pickup or dine-in options instead
                </p>
              </>
            )}
          </div>
        )}

        <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="bg-[#121212] border border-white/10 p-1 h-auto inline-flex flex-nowrap gap-2 min-w-max">
              {displayCategories.map(cat => (
                <TabsTrigger 
                  key={cat} 
                  value={cat}
                  className={`data-[state=active]:bg-[#FF4500] data-[state=active]:text-white rounded-full px-6 py-2 text-sm font-medium whitespace-nowrap ${cat === 'Favorites' ? 'flex items-center gap-1' : ''}`}
                  data-testid={`category-${cat.toLowerCase()}`}
                >
                  {cat === 'Favorites' && <Heart className="w-4 h-4 fill-current" />}
                  {cat}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </Tabs>
      </div>

      {/* Products Grid */}
      <div className="container mx-auto px-4 pb-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map(product => (
            <Card 
              key={product.id} 
              className="product-card bg-[#121212] border-white/10 overflow-hidden group hover:border-[#FF4500]/50 cursor-pointer"
              data-testid={`product-${product.id}`}
              onClick={() => {
                setSelectedProduct(product);
                setSelectedAddons([]);
                setSelectedCustomizations({});
                setItemNotes('');
                setSelectedAddonProducts([]);
              }}
            >
              <div className="aspect-[4/3] overflow-hidden bg-black/20 relative">
                <img 
                  src={getImageUrl(product.image)} 
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                {/* Favorite Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(product.id);
                  }}
                  className={`absolute top-2 left-2 p-2 rounded-full transition-all ${
                    favorites.has(product.id) 
                      ? 'bg-red-500 text-white' 
                      : 'bg-black/50 text-white hover:bg-red-500/80'
                  }`}
                  data-testid={`favorite-${product.id}`}
                >
                  <Heart className={`w-4 h-4 ${favorites.has(product.id) ? 'fill-current' : ''}`} />
                </button>
                {/* Top Rated Badge */}
                {product.top_rated && (
                  <div className="absolute top-2 left-12 bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3 fill-current" />
                    Top Rated
                  </div>
                )}
                {/* Rating Display */}
                {product.average_rating > 0 && (
                  <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                    {product.average_rating.toFixed(1)}
                  </div>
                )}
              </div>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>
                    {product.name}
                  </h3>
                  {/* Show tags if available */}
                  {product.tags && product.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {product.tags.slice(0, 2).map((tag, idx) => (
                        <Badge 
                          key={idx}
                          className="text-xs px-2 py-0.5"
                          style={{ 
                            backgroundColor: 'rgba(255, 69, 0, 0.2)', 
                            color: '#FF4500',
                            borderColor: 'rgba(255, 69, 0, 0.3)'
                          }}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-gray-400 text-sm mb-1 line-clamp-2">
                  {product.description}
                </p>
                <button
                  className="text-[#FF4500] text-xs font-medium hover:underline mb-3"
                  data-testid={`more-${product.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedProduct(product);
                  }}
                >
                  View details
                </button>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-[#FF4500]">
                    {formatCurrency(product.price)}
                  </span>
                  <Button 
                    onClick={(e) => {
                      e.stopPropagation();
                      // If product has effective_addons (from global system) or addons or customizations, open dialog
                      const hasAddons = (product.effective_addons && product.effective_addons.length > 0) || 
                                       (product.addons && product.addons.length > 0);
                      const hasCustomizations = (product.effective_customization_groups && product.effective_customization_groups.length > 0) ||
                                               (product.customization_groups && product.customization_groups.length > 0);
                      // Also open dialog if there are add-on products available
                      if (hasAddons || hasCustomizations || addonProducts.length > 0) {
                        setSelectedProduct(product);
                        setSelectedAddonProducts([]);
                      } else {
                        addToCart(product);
                      }
                    }}
                    className="btn-primary bg-[#FF4500] hover:bg-[#FF5722] text-white rounded-full font-bold uppercase tracking-wider"
                    disabled={!product.available || product.stock === 0}
                    data-testid={`add-to-cart-${product.id}`}
                  >
                    Add to Cart
                  </Button>
                </div>
                {product.stock < 10 && product.stock > 0 && (
                  <p className="text-yellow-400 text-xs mt-2">Only {product.stock} left!</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Product Detail Dialog */}
      <Dialog open={!!selectedProduct} onOpenChange={(open) => !open && setSelectedProduct(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedProduct && (
            <>
              <div className="aspect-video overflow-hidden rounded-lg mb-4 -mx-6 -mt-6">
                <img 
                  src={getImageUrl(selectedProduct.image)} 
                  alt={selectedProduct.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>
                  {selectedProduct.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {selectedProduct.tags && selectedProduct.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedProduct.tags.map((tag, idx) => (
                      <Badge 
                        key={idx}
                        className="text-xs px-2 py-1"
                        style={{ 
                          backgroundColor: 'rgba(255, 69, 0, 0.2)', 
                          color: '#FF4500',
                          borderColor: 'rgba(255, 69, 0, 0.3)'
                        }}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-gray-300 text-sm leading-relaxed">
                  {selectedProduct.description}
                </p>
                
                {/* Add-ons Section - Use effective_addons (global system) if available, fallback to addons */}
                {((selectedProduct.effective_addons && selectedProduct.effective_addons.length > 0) || 
                  (selectedProduct.addons && selectedProduct.addons.length > 0)) && (
                  <div className="border-t border-white/10 pt-4 mt-4">
                    <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <span className="text-[#FF4500]">🔥</span> Add Finishes
                    </h4>
                    <div className="space-y-2">
                      {(selectedProduct.effective_addons || selectedProduct.addons || []).map((addon, idx) => (
                        <label 
                          key={idx}
                          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                            selectedAddons.some(a => a.name === addon.name)
                              ? 'bg-[#FF4500]/20 border border-[#FF4500]/50'
                              : 'bg-white/5 border border-white/10 hover:border-white/30'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={selectedAddons.some(a => a.name === addon.name)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedAddons([...selectedAddons, addon]);
                                } else {
                                  setSelectedAddons(selectedAddons.filter(a => a.name !== addon.name));
                                }
                              }}
                              className="w-4 h-4 accent-[#FF4500]"
                            />
                            <span className="text-white">{addon.name}</span>
                          </div>
                          <span className="text-[#00E5FF] font-medium">+{formatCurrency(addon.price)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Customization Groups Section - Use effective_customization_groups if available */}
                {((selectedProduct.effective_customization_groups && selectedProduct.effective_customization_groups.length > 0) ||
                  (selectedProduct.customization_groups && selectedProduct.customization_groups.length > 0)) && (
                  <div className="border-t border-white/10 pt-4 mt-4 space-y-4">
                    {(selectedProduct.effective_customization_groups || selectedProduct.customization_groups || []).map((group, gIdx) => (
                      <div key={gIdx}>
                        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                          <span className="text-[#00E5FF]">⚡</span> 
                          {group.name}
                          {group.required && <span className="text-red-400 text-xs">(Required)</span>}
                        </h4>
                        <div className="space-y-2">
                          {(group.options || []).map((option, oIdx) => (
                            <label 
                              key={oIdx}
                              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                                selectedCustomizations[group.name]?.name === option.name
                                  ? 'bg-[#00E5FF]/20 border border-[#00E5FF]/50'
                                  : 'bg-white/5 border border-white/10 hover:border-white/30'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <input
                                  type="radio"
                                  name={`customization-${gIdx}`}
                                  checked={selectedCustomizations[group.name]?.name === option.name}
                                  onChange={() => {
                                    setSelectedCustomizations({
                                      ...selectedCustomizations,
                                      [group.name]: option
                                    });
                                  }}
                                  className="w-4 h-4 accent-[#00E5FF]"
                                />
                                <span className="text-white">{option.name}</span>
                              </div>
                              {option.price > 0 && (
                                <span className="text-[#00E5FF] font-medium">+{formatCurrency(option.price)}</span>
                              )}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Special Instructions */}
                <div className="border-t border-white/10 pt-4 mt-4">
                  <label className="text-white text-sm mb-2 block">Special Instructions (Optional)</label>
                  <textarea
                    value={itemNotes}
                    onChange={(e) => setItemNotes(e.target.value)}
                    placeholder="E.g., Extra spicy, no onions..."
                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white text-sm placeholder-gray-500 focus:border-[#FF4500]/50 focus:outline-none"
                    rows={2}
                  />
                </div>
                
                {/* Add-on Products Section (Cross-sell) */}
                {addonProducts.length > 0 && (
                  <div className="border-t border-white/10 pt-4 mt-4">
                    <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <span className="text-[#FF4500]">🍚</span> Complete Your Meal
                    </h4>
                    <div className="space-y-2">
                      {addonProducts.map(product => (
                        <label 
                          key={product.id}
                          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                            selectedAddonProducts.some(p => p.id === product.id)
                              ? 'bg-[#FF4500]/20 border border-[#FF4500]/50'
                              : 'bg-white/5 border border-white/10 hover:border-white/30'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={selectedAddonProducts.some(p => p.id === product.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedAddonProducts([...selectedAddonProducts, product]);
                                } else {
                                  setSelectedAddonProducts(selectedAddonProducts.filter(p => p.id !== product.id));
                                }
                              }}
                              className="w-4 h-4 accent-[#FF4500]"
                            />
                            <img 
                              src={product.image} 
                              alt={product.name}
                              className="w-10 h-10 rounded-lg object-cover"
                            />
                            <div>
                              <span className="text-white text-sm font-medium block">{product.name}</span>
                              <span className="text-gray-400 text-xs">{product.description?.substring(0, 40)}...</span>
                            </div>
                          </div>
                          <span className="text-[#FF4500] font-medium">+{formatCurrency(product.price)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Price and Add to Cart */}
                <div className="flex items-center justify-between pt-4 border-t border-white/10 mt-4">
                  <div>
                    <span className="text-gray-400 text-xs block">Total</span>
                    <span className="text-3xl font-bold text-[#FF4500]">
                      {formatCurrency(
                        selectedProduct.price + 
                        selectedAddons.reduce((sum, a) => sum + (a.price || 0), 0) +
                        Object.values(selectedCustomizations).reduce((sum, c) => sum + (c.price || 0), 0) +
                        selectedAddonProducts.reduce((sum, p) => sum + (p.price || 0), 0)
                      )}
                    </span>
                  </div>
                  <Button 
                    onClick={() => {
                      // Check required customizations - use effective_customization_groups if available
                      const customizationGroups = selectedProduct.effective_customization_groups || selectedProduct.customization_groups || [];
                      const missingRequired = customizationGroups
                        .filter(g => g.required && !selectedCustomizations[g.name]);
                      
                      if (missingRequired.length > 0) {
                        toast.error(`Please select: ${missingRequired.map(g => g.name).join(', ')}`);
                        return;
                      }
                      
                      // Add main product to cart
                      addToCart(selectedProduct, selectedAddons, selectedCustomizations, itemNotes);
                      
                      // Add selected add-on products to cart as separate items
                      if (selectedAddonProducts.length > 0) {
                        addAddonProductsToCart(selectedAddonProducts);
                      }
                      
                      setSelectedProduct(null);
                      setSelectedAddons([]);
                      setSelectedCustomizations({});
                      setItemNotes('');
                      setSelectedAddonProducts([]);
                    }}
                    className="bg-[#FF4500] hover:bg-[#FF5722] text-white rounded-full font-bold uppercase tracking-wider px-8"
                    disabled={!selectedProduct.available || selectedProduct.stock === 0}
                    data-testid="add-to-cart-dialog"
                  >
                    Add to Cart
                  </Button>
                </div>
                {selectedProduct.stock < 10 && selectedProduct.stock > 0 && (
                  <p className="text-yellow-400 text-sm">Only {selectedProduct.stock} left in stock!</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Cart Drawer */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>
              Your Cart
            </DialogTitle>
          </DialogHeader>
          
          {cart.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-16 h-16 mx-auto text-gray-600 mb-4" strokeWidth={1.5} />
              <p className="text-gray-400">Your cart is empty</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map(item => (
                <div key={item.cart_key || item.product_id} className="bg-[#1A1A1A] rounded-lg p-4">
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <h4 className="font-semibold text-white">{item.product_name}</h4>
                      {/* Show selected add-ons */}
                      {item.selected_addons && item.selected_addons.length > 0 && (
                        <div className="mt-1">
                          {item.selected_addons.map((addon, idx) => (
                            <span key={idx} className="text-xs text-[#FF4500] mr-2">
                              +{addon.name}
                            </span>
                          ))}
                        </div>
                      )}
                      {/* Show selected customizations */}
                      {item.selected_customizations && item.selected_customizations.length > 0 && (
                        <div className="mt-1">
                          {item.selected_customizations.map((cust, idx) => (
                            <span key={idx} className="text-xs text-[#00E5FF] mr-2">
                              {cust.option}
                            </span>
                          ))}
                        </div>
                      )}
                      {/* Show item notes */}
                      {item.item_notes && (
                        <p className="text-xs text-gray-400 mt-1 italic">📝 {item.item_notes}</p>
                      )}
                      <p className="text-[#FF4500] font-bold mt-1">{formatCurrency(item.price)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => updateQuantity(item.cart_key || item.product_id, -1)}
                        className="w-8 h-8 p-0 border-white/20 hover:bg-white/5"
                      >
                        -
                      </Button>
                      <span className="w-8 text-center font-mono">{item.quantity}</span>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => updateQuantity(item.cart_key || item.product_id, 1)}
                        className="w-8 h-8 p-0 border-white/20 hover:bg-white/5"
                      >
                        +
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => removeFromCart(item.cart_key || item.product_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-lg font-bold">Total</span>
                  <span className="text-2xl font-bold text-[#FF4500]">
                    {formatCurrency(calculateTotal())}
                  </span>
                </div>
                
                {/* Estimated Delivery Time in Cart */}
                <div className="mb-4 bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-orange-400" />
                      <span className="text-orange-400 font-medium text-sm">Est. Delivery</span>
                    </div>
                    <span className="text-orange-400 font-bold text-lg">35-45 mins</span>
                  </div>
                </div>
                
                <Button 
                  onClick={() => {
                    // Check if outlet is accepting orders
                    if (!outletStatus.isAcceptingOrders) {
                      toast.error(`We're not accepting orders right now. Ordering hours: ${outletStatus.orderingStartTime} - ${outletStatus.orderingEndTime}`);
                      return;
                    }
                    // Check if location error (out of range or all outlets closed) - only block for delivery
                    if (locationError && checkoutData.order_type === 'delivery') {
                      if (locationError === 'all_outlets_closed') {
                        toast.error('All outlets in your area are currently closed. Please try again later.');
                      } else {
                        toast.error("We don't deliver to your location. Please select pickup or dine-in.");
                      }
                      return;
                    }
                    // Check if user is logged in
                    if (!isLoggedIn) {
                      toast.error('Please login to place an order');
                      setShowCart(false);
                      setShowLogin(true);
                      return;
                    }
                    setShowCart(false);
                    setShowCheckout(true);
                  }}
                  className="w-full btn-primary bg-[#FF4500] hover:bg-[#FF5722] text-white rounded-full py-6 font-bold uppercase tracking-wider text-lg"
                  data-testid="proceed-to-checkout"
                >
                  {isLoggedIn ? 'Proceed to Checkout' : 'Login to Checkout'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent 
          className="bg-[#121212] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => {
            // Prevent dialog from closing when clicking on Google Places dropdown
            if (e.target.closest('.pac-container')) {
              e.preventDefault();
            }
          }}
          onPointerDownOutside={(e) => {
            // Prevent dialog from closing when clicking on Google Places dropdown
            if (e.target.closest('.pac-container')) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>
              Checkout
            </DialogTitle>
          </DialogHeader>
          
          {/* Outlet Indicator */}
          {selectedOutlet && (
            <div className="bg-[#FF4500]/10 border border-[#FF4500]/30 rounded-lg p-3 mb-2">
              <div className="flex items-center gap-2 text-sm text-gray-400 mb-1">
                {checkoutData.order_type === 'delivery' ? (
                  <><Truck className="w-4 h-4 text-[#00E5FF]" /> Delivering from</>
                ) : checkoutData.order_type === 'pickup' ? (
                  <><Store className="w-4 h-4 text-green-400" /> Pickup at</>
                ) : checkoutData.order_type === 'dine_in' ? (
                  <><UtensilsCrossed className="w-4 h-4 text-orange-400" /> Dine-in at</>
                ) : (
                  <><Store className="w-4 h-4" /> Ordering from</>
                )}
              </div>
              <p className="text-white font-bold text-lg" style={{ fontFamily: 'Bebas Neue' }}>
                {selectedOutlet.name}
              </p>
              {selectedOutlet.address && (
                <p className="text-gray-400 text-xs mt-1">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  {selectedOutlet.address}
                </p>
              )}
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <Label className="text-white mb-2 block">Order Type *</Label>
              <RadioGroup 
                value={checkoutData.order_type} 
                onValueChange={(value) => setCheckoutData({...checkoutData, order_type: value})}
              >
                <div className="flex items-center space-x-2 bg-white/5 p-3 rounded-lg">
                  <RadioGroupItem value="delivery" id="delivery" />
                  <Label htmlFor="delivery" className="flex-1 cursor-pointer">
                    <MapPin className="inline w-4 h-4 mr-2" />
                    Delivery
                  </Label>
                </div>
                <div className="flex items-center space-x-2 bg-white/5 p-3 rounded-lg">
                  <RadioGroupItem value="pickup" id="pickup" />
                  <Label htmlFor="pickup" className="flex-1 cursor-pointer">
                    <ShoppingCart className="inline w-4 h-4 mr-2" />
                    Pickup
                  </Label>
                </div>
                <div className="flex items-center space-x-2 bg-white/5 p-3 rounded-lg">
                  <RadioGroupItem value="dine_in" id="dine_in" />
                  <Label htmlFor="dine_in" className="flex-1 cursor-pointer">
                    <UtensilsCrossed className="inline w-4 h-4 mr-2" />
                    Dine In
                  </Label>
                </div>
              </RadioGroup>
            </div>
            
            {checkoutData.order_type === 'delivery' && (
              <div className="space-y-3">
                <Label className="text-white mb-2 block">Delivery Address *</Label>
                
                {/* Saved Addresses Selection */}
                {savedAddresses.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-400">Choose a saved address or add new</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {savedAddresses.map((addr) => (
                        <div 
                          key={addr.id}
                          onClick={() => selectSavedAddress(addr)}
                          className={`p-3 rounded-lg cursor-pointer border transition-all ${
                            selectedAddressId === addr.id 
                              ? 'border-[#FF4500] bg-[#FF4500]/10' 
                              : 'border-white/10 bg-white/5 hover:border-white/30'
                          }`}
                          data-testid={`saved-address-${addr.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  addr.label === 'Home' ? 'bg-blue-500/20 text-blue-400' :
                                  addr.label === 'Work' ? 'bg-green-500/20 text-green-400' :
                                  'bg-purple-500/20 text-purple-400'
                                }`}>
                                  {addr.label}
                                </span>
                                {addr.is_default && (
                                  <span className="text-xs text-[#FF4500]">Default</span>
                                )}
                              </div>
                              <p className="text-white text-sm mt-1">{addr.name}</p>
                              <p className="text-gray-400 text-xs">{addr.phone}</p>
                              <p className="text-gray-400 text-xs mt-1">{addr.full_address}</p>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteSavedAddress(addr.id); }}
                              className="text-red-400 hover:text-red-300 p-1"
                              title="Delete address"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Add New Address Button */}
                    <button
                      onClick={handleNewAddress}
                      className={`w-full p-3 rounded-lg border border-dashed transition-all flex items-center justify-center gap-2 ${
                        showNewAddressForm 
                          ? 'border-[#FF4500] bg-[#FF4500]/10 text-[#FF4500]' 
                          : 'border-white/30 text-gray-400 hover:border-white/50 hover:text-white'
                      }`}
                      data-testid="add-new-address-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Add New Address
                    </button>
                  </div>
                )}
                
                {/* New Address Form - Show if no saved addresses OR user clicked "Add New" */}
                {(savedAddresses.length === 0 || showNewAddressForm) && (
                  <div className="space-y-3 pt-2">
                    {savedAddresses.length > 0 && (
                      <p className="text-sm text-[#FF4500]">Enter new address details:</p>
                    )}
                    
                    {/* Name for this address - MANDATORY */}
                    <div>
                      <Label className="text-white mb-1 block text-sm">Name *</Label>
                      <Input 
                        value={checkoutData.customer_name || ''}
                        onChange={(e) => setCheckoutData({...checkoutData, customer_name: e.target.value})}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="Enter your name"
                        required
                        data-testid="checkout-name"
                      />
                    </div>
                    
                    {/* Phone for this address - MANDATORY */}
                    <div>
                      <Label className="text-white mb-1 block text-sm">Phone *</Label>
                      <div className="flex items-center gap-0">
                        <div className="bg-white/10 border border-white/10 border-r-0 rounded-l-md px-3 py-2 text-white font-medium h-10 flex items-center text-sm">
                          +91
                        </div>
                        <Input 
                          type="tel"
                          inputMode="numeric"
                          maxLength={10}
                          value={checkoutData.customer_phone.replace('+91', '')}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setCheckoutData({...checkoutData, customer_phone: '+91' + digits});
                          }}
                          className="bg-white/5 border-white/10 text-white rounded-l-none flex-1"
                          placeholder="9876543210"
                          required
                          data-testid="checkout-phone"
                        />
                      </div>
                    </div>
                    
                    {/* Google Places Autocomplete Search */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
                      <input
                        ref={addressInputRef}
                        id="google-places-input"
                        type="text"
                        autoComplete="off"
                        placeholder="Search for your location..."
                        className="w-full bg-white/5 border border-white/10 text-white rounded-md pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF4500]/50 focus:border-[#FF4500]/50"
                        data-testid="checkout-address-search"
                      />
                    </div>
                    
                    {/* Pin on Map Button */}
                    <button
                      onClick={openMapPicker}
                      className="w-full p-2 rounded-lg border border-white/20 text-gray-300 hover:border-[#00E5FF] hover:text-[#00E5FF] transition-all flex items-center justify-center gap-2"
                      data-testid="pin-on-map-btn"
                    >
                      <MapPin className="w-4 h-4" />
                      Pin Exact Location on Map
                    </button>
                    
                    {/* Location confirmation */}
                    {checkoutData.latitude && checkoutData.longitude && (
                      <div className="flex items-center gap-2 text-green-400 text-sm bg-green-500/10 p-2 rounded-lg">
                        <MapPin className="w-4 h-4" />
                        <span>Location coordinates captured</span>
                        <a 
                          href={`https://www.google.com/maps?q=${checkoutData.latitude},${checkoutData.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-auto text-[#00E5FF] hover:underline"
                        >
                          View on Map →
                        </a>
                      </div>
                    )}
                    
                    {/* Flat/House Number */}
                    <Input 
                      value={checkoutData.flat_house || ''}
                      onChange={(e) => setCheckoutData({...checkoutData, flat_house: e.target.value})}
                      className="bg-white/5 border-white/10 text-white"
                      placeholder="Flat / House No. / Building Name"
                      data-testid="checkout-flat"
                    />
                    
                    {/* Street/Area */}
                    <Input 
                      value={checkoutData.street_area || ''}
                      onChange={(e) => setCheckoutData({...checkoutData, street_area: e.target.value})}
                      className="bg-white/5 border-white/10 text-white"
                      placeholder="Street / Area / Colony"
                      data-testid="checkout-street"
                    />
                    
                    {/* Landmark */}
                    <Input 
                      value={checkoutData.landmark || ''}
                      onChange={(e) => setCheckoutData({...checkoutData, landmark: e.target.value})}
                      className="bg-white/5 border-white/10 text-white"
                      placeholder="Landmark (Optional)"
                      data-testid="checkout-landmark"
                    />
                    
                    {/* City and Pincode */}
                    <div className="grid grid-cols-2 gap-3">
                      <Input 
                        value={checkoutData.city || ''}
                        onChange={(e) => setCheckoutData({...checkoutData, city: e.target.value})}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="City"
                        data-testid="checkout-city"
                      />
                      <Input 
                        value={checkoutData.pincode || ''}
                        onChange={(e) => setCheckoutData({...checkoutData, pincode: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="Pincode"
                        maxLength={6}
                        inputMode="numeric"
                        data-testid="checkout-pincode"
                      />
                    </div>
                    
                    {/* Save Address Option */}
                    <div className="flex items-center gap-3 pt-2">
                      <input
                        type="checkbox"
                        id="save-address"
                        checked={saveThisAddress}
                        onChange={(e) => setSaveThisAddress(e.target.checked)}
                        className="rounded border-white/30"
                      />
                      <label htmlFor="save-address" className="text-sm text-gray-300 cursor-pointer">
                        Save this address for future orders
                      </label>
                    </div>
                    
                    {saveThisAddress && (
                      <div className="flex items-center gap-2">
                        <Label className="text-gray-400 text-sm">Label:</Label>
                        <select
                          value={addressLabel}
                          onChange={(e) => setAddressLabel(e.target.value)}
                          className="bg-white/5 border border-white/10 text-white rounded-md px-3 py-1.5 text-sm"
                        >
                          <option value="Home">🏠 Home</option>
                          <option value="Work">🏢 Work</option>
                          <option value="Other">📍 Other</option>
                        </select>
                        <button
                          onClick={saveCurrentAddress}
                          className="ml-auto px-3 py-1.5 bg-[#FF4500] text-white rounded-md text-sm hover:bg-[#FF4500]/80"
                        >
                          Save Address
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Pickup Outlet Selection */}
            {checkoutData.order_type === 'pickup' && selectedOutlet && (
              <div className="space-y-4">
                <div>
                  <Label className="text-white mb-2 block">Pickup Location</Label>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-[#00E5FF] flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <p className="text-white font-semibold mb-1">{selectedOutlet.name}</p>
                        <p className="text-sm text-gray-400 mb-2">{selectedOutlet.address}</p>
                        <p className="text-sm text-gray-400 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {selectedOutlet.phone}
                        </p>
                        {selectedOutlet.latitude && selectedOutlet.longitude && (
                          <a
                            href={`https://www.google.com/maps?q=${selectedOutlet.latitude},${selectedOutlet.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#00E5FF] text-sm hover:underline mt-2 inline-block"
                          >
                            View on Map →
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Location Verification Notice */}
                  <div className="mt-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                    <p className="text-yellow-400 text-xs flex items-center gap-2">
                      <LocateFixed className="w-4 h-4" />
                      Please ensure you are at or near the outlet for pickup orders
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setGettingLocation(true);
                        fetchGPSLocation();
                      }}
                      className="mt-2 text-xs border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                      disabled={gettingLocation}
                    >
                      {gettingLocation ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          Refreshing...
                        </>
                      ) : (
                        <>
                          <Navigation className="w-3 h-3 mr-1" />
                          Refresh My Location
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                
                {/* Customer Details for Pickup */}
                <div className="space-y-3">
                  <Label className="text-white mb-1 block text-sm">Your Details</Label>
                  <div>
                    <Input 
                      value={checkoutData.customer_name || ''}
                      onChange={(e) => setCheckoutData({...checkoutData, customer_name: e.target.value})}
                      className="bg-white/5 border-white/10 text-white"
                      placeholder="Your name"
                      data-testid="pickup-name"
                    />
                  </div>
                  <div className="flex items-center gap-0">
                    <div className="bg-white/10 border border-white/10 border-r-0 rounded-l-md px-3 py-2 text-white font-medium h-10 flex items-center text-sm">
                      +91
                    </div>
                    <Input 
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={checkoutData.customer_phone.replace('+91', '')}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setCheckoutData({...checkoutData, customer_phone: '+91' + digits});
                      }}
                      className="bg-white/5 border-white/10 text-white rounded-l-none flex-1"
                      placeholder="Phone number"
                      data-testid="pickup-phone"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Dine In Location */}
            {checkoutData.order_type === 'dine_in' && selectedOutlet && (
              <div className="space-y-4">
                <div>
                  <Label className="text-white mb-2 block">Dine In Location</Label>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <UtensilsCrossed className="w-5 h-5 text-[#FF4500] flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <p className="text-white font-semibold mb-1">{selectedOutlet.name}</p>
                        <p className="text-sm text-gray-400 mb-2">{selectedOutlet.address}</p>
                        <p className="text-sm text-gray-400 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {selectedOutlet.phone}
                        </p>
                        <p className="text-sm text-green-400 mt-2">✓ No packaging charges for dine in</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Location Verification Notice for Dine-in */}
                  <div className="mt-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                    <p className="text-yellow-400 text-xs flex items-center gap-2">
                      <LocateFixed className="w-4 h-4" />
                      Dine-in orders can only be placed when you are at the restaurant
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setGettingLocation(true);
                        fetchGPSLocation();
                      }}
                      className="mt-2 text-xs border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                      disabled={gettingLocation}
                    >
                      {gettingLocation ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          Refreshing...
                        </>
                      ) : (
                        <>
                          <Navigation className="w-3 h-3 mr-1" />
                          Verify My Location
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                
                {/* Customer Details for Dine In */}
                <div className="space-y-3">
                  <Label className="text-white mb-1 block text-sm">Your Details</Label>
                  <div>
                    <Input 
                      value={checkoutData.customer_name || ''}
                      onChange={(e) => setCheckoutData({...checkoutData, customer_name: e.target.value})}
                      className="bg-white/5 border-white/10 text-white"
                      placeholder="Your name"
                      data-testid="dinein-name"
                    />
                  </div>
                  <div className="flex items-center gap-0">
                    <div className="bg-white/10 border border-white/10 border-r-0 rounded-l-md px-3 py-2 text-white font-medium h-10 flex items-center text-sm">
                      +91
                    </div>
                    <Input 
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={checkoutData.customer_phone.replace('+91', '')}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setCheckoutData({...checkoutData, customer_phone: '+91' + digits});
                      }}
                      className="bg-white/5 border-white/10 text-white rounded-l-none flex-1"
                      placeholder="Phone number"
                      data-testid="dinein-phone"
                    />
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <Label className="text-white mb-2 block">Payment Method *</Label>
              <RadioGroup 
                value={checkoutData.payment_method} 
                onValueChange={(value) => setCheckoutData({...checkoutData, payment_method: value})}
                className="space-y-2"
              >
                {paymentSettings.cod_enabled && (
                  <div className="flex items-center space-x-2 bg-white/5 p-3 rounded-lg hover:bg-white/10 transition-colors">
                    <RadioGroupItem value="cod" id="cod" />
                    <Label htmlFor="cod" className="flex-1 cursor-pointer flex items-center gap-2">
                      <span>💵</span>
                      <span>Cash on Delivery</span>
                    </Label>
                  </div>
                )}
                {paymentSettings.upi_enabled && (
                  <div className="bg-white/5 p-3 rounded-lg hover:bg-white/10 transition-colors">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="upi" id="upi" />
                      <Label htmlFor="upi" className="flex-1 cursor-pointer">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span>📱</span>
                            <span>UPI Payment</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-400">
                            <span className="bg-white/10 px-2 py-0.5 rounded">GPay</span>
                            <span className="bg-white/10 px-2 py-0.5 rounded">PhonePe</span>
                            <span className="bg-white/10 px-2 py-0.5 rounded">Paytm</span>
                          </div>
                        </div>
                        <p className="text-xs text-green-400 mt-1 ml-6">✓ Opens your UPI app directly for quick payment</p>
                      </Label>
                    </div>
                  </div>
                )}
                {paymentSettings.razorpay_enabled && (
                  <div className="flex items-center space-x-2 bg-white/5 p-3 rounded-lg hover:bg-white/10 transition-colors">
                    <RadioGroupItem value="card" id="card" />
                    <Label htmlFor="card" className="flex-1 cursor-pointer flex items-center gap-2">
                      <span>💳</span>
                      <span>Credit/Debit Card</span>
                    </Label>
                  </div>
                )}
              </RadioGroup>
            </div>
            
            {/* Loyalty Points Redemption */}
            {isLoggedIn && loyaltyBalance?.can_redeem && loyaltyConfig?.loyalty_enabled !== false && (
              <div className="border-t border-white/10 pt-4">
                <Label className="text-white mb-2 block flex items-center gap-2">
                  <Gift className="w-4 h-4 text-[#FF4500]" />
                  Redeem Loyalty Points
                </Label>
                <div className="bg-[#FF4500]/10 border border-[#FF4500]/30 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">Available: {loyaltyBalance.balance} pts</span>
                    <span className="text-[#00E5FF] text-sm">Worth {formatCurrency(loyaltyBalance.points_value)}</span>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min="0"
                      max={loyaltyBalance.balance}
                      value={pointsToRedeem || ''}
                      onChange={(e) => handlePointsChange(e.target.value)}
                      placeholder="0"
                      className="bg-white/5 border-white/10 text-white flex-1"
                      data-testid="points-input"
                    />
                    {pointsToRedeem > 0 ? (
                      <Button
                        onClick={handleClearPoints}
                        variant="outline"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        Clear
                      </Button>
                    ) : (
                      <Button
                        onClick={handleUseAllPoints}
                        variant="outline"
                        className="border-[#FF4500]/50 text-[#FF4500] hover:bg-[#FF4500]/10"
                      >
                        Use All
                      </Button>
                    )}
                  </div>
                  {pointsToRedeem > 0 && (
                    <p className="text-green-400 text-sm mt-2">
                      Saving {formatCurrency(pointsDiscount)} with {pointsToRedeem} points
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {/* Coupon Code Section */}
            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-white">Have a coupon code?</Label>
                <button
                  onClick={() => {
                    fetchAvailableCoupons();
                    setShowAvailableCoupons(true);
                  }}
                  className="text-[#FF4500] text-sm hover:underline flex items-center gap-1"
                  data-testid="view-coupons-btn"
                >
                  <Tag className="w-3 h-3" />
                  View Available Coupons
                </button>
              </div>
              {appliedCoupon ? (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-green-400 font-semibold">{appliedCoupon.code} applied!</p>
                    <p className="text-sm text-gray-400">You save {formatCurrency(appliedCoupon.discount_amount)}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleRemoveCoupon}
                    className="text-red-400 hover:text-red-300"
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter code"
                    className="bg-white/5 border-white/10 text-white flex-1 font-mono"
                    data-testid="coupon-input"
                  />
                  <Button
                    onClick={handleApplyCoupon}
                    disabled={couponLoading || !couponCode}
                    variant="outline"
                    className="border-[#FF4500]/50 text-[#FF4500] hover:bg-[#FF4500]/10"
                    data-testid="apply-coupon-btn"
                  >
                    {couponLoading ? '...' : 'Apply'}
                  </Button>
                </div>
              )}
            </div>
            
            <div className="border-t border-white/10 pt-4">
              {/* Zomato-style Bill Summary */}
              <div 
                className="bg-white/5 rounded-lg p-4 mb-4 cursor-pointer"
                onClick={() => setShowBillDetails(!showBillDetails)}
                data-testid="bill-summary-toggle"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div>
                      <span className="text-white font-semibold">Total Bill</span>
                      <span className="text-gray-400 text-sm ml-2">
                        {appliedCoupon && <span className="line-through mr-1">{formatCurrency(grandTotal + (appliedCoupon?.discount_amount || 0))}</span>}
                        <span className="text-[#FF4500] font-bold">{formatCurrency(Math.max(0, grandTotal))}</span>
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {(appliedCoupon || pointsDiscount > 0) && (
                      <span className="text-green-400 text-sm font-medium">
                        You saved {formatCurrency((appliedCoupon?.discount_amount || 0) + pointsDiscount)}
                      </span>
                    )}
                    <svg 
                      className={`w-5 h-5 text-gray-400 transition-transform ${showBillDetails ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-500 text-xs mt-1">Incl. taxes and charges</p>
              </div>

              {/* Expanded Bill Details */}
              {showBillDetails && (
                <div className="bg-white rounded-lg p-4 mb-4 text-black" data-testid="bill-details">
                  <h3 className="text-lg font-bold mb-4">Bill Summary</h3>
                  
                  <div className="space-y-3">
                    {/* Item Total */}
                    <div className="flex justify-between">
                      <span className="text-gray-700">Item total</span>
                      <span className="font-medium">{formatCurrency(cartTotal)}</span>
                    </div>
                    
                    {/* Packaging & Handling Charges */}
                    {packagingFee > 0 && (
                      <div className="flex justify-between">
                        <div>
                          <span className="text-gray-700">Packaging & handling</span>
                          <p className="text-xs text-gray-500">Based on items in cart</p>
                        </div>
                        <span className="font-medium">{formatCurrency(packagingFee)}</span>
                      </div>
                    )}
                    
                    {/* Delivery Partner Fee */}
                    {checkoutData.order_type === 'delivery' && (
                      <div className="flex justify-between">
                        <div>
                          <span className="text-gray-700">Delivery partner fee</span>
                          {deliveryFeeInfo?.distance && (
                            <p className="text-xs text-gray-500">{deliveryFeeInfo.distance} km from shop</p>
                          )}
                        </div>
                        {deliveryFee === 0 ? (
                          <div className="text-right">
                            <span className="line-through text-gray-400 mr-1">{formatCurrency(calculatedDeliveryFee || siteSettings.delivery_fee)}</span>
                            <span className="text-green-600 font-medium">FREE</span>
                          </div>
                        ) : deliveryFeeInfo?.type === 'out_of_range' ? (
                          <span className="text-red-500 font-medium">Out of range</span>
                        ) : (
                          <span className="font-medium">{formatCurrency(deliveryFee)}</span>
                        )}
                      </div>
                    )}
                    
                    {/* Out of range warning */}
                    {deliveryFeeInfo?.type === 'out_of_range' && (
                      <div className="bg-red-50 text-red-600 text-sm p-2 rounded">
                        ⚠️ {deliveryFeeInfo.message}
                      </div>
                    )}
                    
                    {/* GST */}
                    {gstAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-700">GST (govt. taxes)</span>
                        <span className="font-medium">{formatCurrency(gstAmount)}</span>
                      </div>
                    )}
                    
                    {/* Coupon Discount */}
                    {appliedCoupon && (
                      <div className="flex justify-between text-green-600">
                        <span>Coupon ({appliedCoupon.code})</span>
                        <span className="font-medium">-{formatCurrency(appliedCoupon.discount_amount)}</span>
                      </div>
                    )}
                    
                    {/* Points Discount */}
                    {pointsDiscount > 0 && (
                      <div className="flex justify-between text-blue-600">
                        <span>Loyalty Points ({pointsToRedeem} pts)</span>
                        <span className="font-medium">-{formatCurrency(pointsDiscount)}</span>
                      </div>
                    )}
                    
                    {/* Total */}
                    <div className="flex justify-between pt-3 border-t border-gray-200">
                      <span className="font-bold text-lg">To Pay</span>
                      <span className="font-bold text-lg">{formatCurrency(Math.max(0, grandTotal))}</span>
                    </div>
                  </div>
                  
                  {/* Savings Banner */}
                  {(appliedCoupon || pointsDiscount > 0 || deliveryFee === 0) && (
                    <div className="mt-4 bg-gradient-to-r from-green-50 to-yellow-50 rounded-lg p-3 flex items-center gap-2">
                      <span className="text-xl">🎉</span>
                      <span className="text-green-700 font-medium text-sm">
                        You saved {formatCurrency((appliedCoupon?.discount_amount || 0) + pointsDiscount + (checkoutData.order_type === 'delivery' && deliveryFee === 0 ? siteSettings.delivery_fee : 0))} on this order!
                      </span>
                    </div>
                  )}
                  
                  {/* Free Delivery Info */}
                  {checkoutData.order_type === 'delivery' && deliveryFee > 0 && (
                    <div className="mt-3 text-sm text-gray-500">
                      Add {formatCurrency(siteSettings.free_delivery_threshold - cartTotal)} more for FREE delivery
                    </div>
                  )}
                  
                  {/* Estimated Delivery Time - With distance calculated */}
                  {checkoutData.order_type === 'delivery' && deliveryFeeInfo?.distance && deliveryFeeInfo?.type !== 'out_of_range' && (
                    <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Clock className="w-5 h-5 text-orange-500" />
                          <span className="text-orange-700 font-medium">Estimated Delivery Time</span>
                        </div>
                        <span className="text-orange-600 font-bold text-xl">
                          {(() => {
                            const distance = parseFloat(deliveryFeeInfo.distance);
                            const prepTime = 20;
                            const deliveryTime = distance < 3 ? 15 : distance <= 6 ? 20 : 25;
                            const buffer = 5;
                            const total = prepTime + deliveryTime + buffer;
                            return `${total - 5}-${total + 5} mins`;
                          })()}
                        </span>
                      </div>
                      <p className="text-xs text-orange-600 mt-2">
                        Prep: ~20 mins • Delivery: ~{parseFloat(deliveryFeeInfo.distance) < 3 ? 15 : parseFloat(deliveryFeeInfo.distance) <= 6 ? 20 : 25} mins ({deliveryFeeInfo.distance} km)
                      </p>
                    </div>
                  )}
                  
                  {/* Estimated Delivery Time - Default before address is entered */}
                  {checkoutData.order_type === 'delivery' && !deliveryFeeInfo?.distance && (
                    <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Clock className="w-5 h-5 text-orange-500" />
                          <span className="text-orange-700 font-medium">Estimated Delivery Time</span>
                        </div>
                        <span className="text-orange-600 font-bold text-xl">35-45 mins</span>
                      </div>
                      <p className="text-xs text-orange-600 mt-2">
                        Prep: ~20 mins • Delivery: ~15-20 mins (based on location)
                      </p>
                    </div>
                  )}
                  
                  {/* Pickup Time Estimate */}
                  {checkoutData.order_type === 'pickup' && (
                    <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Clock className="w-5 h-5 text-green-500" />
                          <span className="text-green-700 font-medium">Ready for Pickup</span>
                        </div>
                        <span className="text-green-600 font-bold text-xl">15-25 mins</span>
                      </div>
                      <p className="text-xs text-green-600 mt-2">
                        Your order will be ready for pickup at the counter
                      </p>
                    </div>
                  )}

                  {/* Dine In Info */}
                  {checkoutData.order_type === 'dine_in' && (
                    <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <UtensilsCrossed className="w-5 h-5 text-orange-500" />
                          <span className="text-orange-700 font-medium">Dine In</span>
                        </div>
                        <span className="text-orange-600 font-bold text-xl">15-25 mins</span>
                      </div>
                      <p className="text-xs text-orange-600 mt-2">
                        Your order will be served fresh at your table
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Out of Range Block for Delivery */}
              {checkoutData.order_type === 'delivery' && deliveryFeeInfo?.type === 'out_of_range' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-center">
                  <p className="text-red-600 font-medium">🚫 Delivery Not Available</p>
                  <p className="text-red-500 text-sm mt-1">
                    Your location is {deliveryFeeInfo.distance}km away - outside our delivery area
                  </p>
                  <p className="text-gray-500 text-xs mt-2">
                    Try pickup instead, or select a different delivery address
                  </p>
                </div>
              )}

              <Button 
                onClick={handleCheckout}
                disabled={loading || (checkoutData.order_type === 'delivery' && deliveryFeeInfo?.type === 'out_of_range')}
                className={`w-full btn-primary text-white rounded-full py-6 font-bold uppercase tracking-wider text-lg ${
                  checkoutData.order_type === 'delivery' && deliveryFeeInfo?.type === 'out_of_range'
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-[#FF4500] hover:bg-[#FF5722]'
                }`}
                data-testid="place-order"
              >
                {loading 
                  ? 'Placing Order...' 
                  : checkoutData.order_type === 'delivery' && deliveryFeeInfo?.type === 'out_of_range'
                    ? 'Delivery Not Available'
                    : `Place Order • ${formatCurrency(Math.max(0, grandTotal))}`
                }
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Login Dialog */}
      <Dialog open={showLogin} onOpenChange={(open) => {
        setShowLogin(open);
        if (!open) {
          setLoginPhone('');
          setLoginOtp('');
          setOtpSent(false);
          setShowReferralInput(false);
          setSignupReferralCode('');
        }
      }}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center" style={{ fontFamily: 'Bebas Neue' }}>
              {otpSent ? 'Enter OTP' : 'Login / Sign Up'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!otpSent ? (
              <>
                <p className="text-gray-400 text-sm text-center">
                  Enter your phone number to continue
                </p>
                <div>
                  <Label htmlFor="login-phone" className="text-white mb-2 block">Phone Number</Label>
                  <div className="flex items-center gap-0">
                    <div className="bg-white/10 border border-white/10 border-r-0 rounded-l-md px-3 py-2 text-white text-lg font-medium h-10 flex items-center">
                      +91
                    </div>
                    <Input 
                      id="login-phone"
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={loginPhone}
                      onChange={(e) => setLoginPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="bg-white/5 border-white/10 text-white text-lg rounded-l-none flex-1"
                      placeholder="9876543210"
                      data-testid="login-phone-input"
                    />
                  </div>
                </div>
                <Button 
                  onClick={handleSendOtp}
                  disabled={authLoading || !loginPhone}
                  className="w-full bg-[#FF4500] hover:bg-[#FF5722] text-white rounded-full py-6 font-bold"
                  data-testid="send-otp-btn"
                >
                  {authLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send OTP'
                  )}
                </Button>
              </>
            ) : (
              <>
                <p className="text-gray-400 text-sm text-center">
                  OTP sent to <span className="text-white">+91 {loginPhone}</span>
                </p>
                <p className="text-gray-500 text-xs text-center">
                  Valid for 5 minutes
                </p>
                <div>
                  <Label htmlFor="login-otp" className="text-white mb-2 block">Enter OTP</Label>
                  <Input 
                    ref={otpInputRef}
                    id="login-otp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={loginOtp}
                    onChange={(e) => setLoginOtp(e.target.value.replace(/\D/g, ''))}
                    className="bg-white/5 border-white/10 text-white text-2xl text-center tracking-[0.5em] font-mono"
                    placeholder="------"
                    data-testid="login-otp-input"
                  />
                </div>
                
                {/* Referral Code Option - Only show if loyalty is enabled */}
                {loyaltyConfig?.loyalty_enabled !== false && (
                <div className="border-t border-white/10 pt-4">
                  {!showReferralInput ? (
                    <button
                      onClick={() => setShowReferralInput(true)}
                      className="w-full text-center text-[#FFD600] text-sm hover:underline flex items-center justify-center gap-2"
                      data-testid="have-referral-btn"
                    >
                      <Star className="w-4 h-4" />
                      Have a referral code?
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-[#FFD600] text-sm flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          Referral Code
                        </Label>
                        <button
                          onClick={() => {
                            setShowReferralInput(false);
                            setSignupReferralCode('');
                          }}
                          className="text-gray-500 text-xs hover:text-gray-400"
                        >
                          Remove
                        </button>
                      </div>
                      <Input
                        value={signupReferralCode}
                        onChange={(e) => setSignupReferralCode(e.target.value.toUpperCase())}
                        placeholder="Enter friend's code"
                        className="bg-[#FFD600]/10 border-[#FFD600]/30 text-white font-mono text-center"
                        data-testid="signup-referral-input"
                      />
                      <p className="text-xs text-gray-500 text-center">
                        Get {loyaltyConfig?.referral_bonus_referee || 50} bonus points (worth ₹{((loyaltyConfig?.referral_bonus_referee || 50) * (loyaltyConfig?.rupees_per_point || 0.25)).toFixed(2)})!
                      </p>
                    </div>
                  )}
                </div>
                )}
                
                <Button 
                  onClick={handleVerifyOtp}
                  disabled={authLoading || loginOtp.length < 6}
                  className="w-full bg-[#FF4500] hover:bg-[#FF5722] text-white rounded-full py-6 font-bold"
                  data-testid="verify-otp-btn"
                >
                  {authLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify & Login'
                  )}
                </Button>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    onClick={() => {
                      setOtpSent(false);
                      setLoginOtp('');
                      setShowReferralInput(false);
                      setSignupReferralCode('');
                    }}
                    className="flex-1 text-gray-400 hover:text-white text-sm"
                  >
                    Change Number
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={handleSendOtp}
                    disabled={authLoading}
                    className="flex-1 text-[#FF4500] hover:text-[#FF5722] text-sm"
                    data-testid="resend-otp-btn"
                  >
                    Resend OTP
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Map Picker Dialog */}
      <Dialog open={showMapPicker} onOpenChange={setShowMapPicker}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#FF4500]" />
              Pin Your Location
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* My Location Button */}
            <div className="space-y-2">
              <button
                onClick={getCurrentLocation}
                disabled={gettingLocation}
                className="w-full p-4 rounded-lg border-2 border-[#00E5FF]/50 bg-[#00E5FF]/10 text-[#00E5FF] hover:bg-[#00E5FF]/20 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                data-testid="my-location-btn"
              >
                {gettingLocation ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span className="text-lg">Detecting your location...</span>
                  </>
                ) : (
                  <>
                    <LocateFixed className="w-6 h-6" />
                    <span className="text-lg font-medium">Use My Current Location</span>
                  </>
                )}
              </button>
              <p className="text-xs text-gray-500 text-center">
                Your browser will ask for permission to access your location
              </p>
            </div>
            
            {/* Interactive Google Map */}
            <div className="space-y-2">
              <div 
                ref={mapContainerRef}
                className="w-full h-[350px] rounded-lg overflow-hidden border border-white/10 bg-gray-900"
              />
              <p className="text-xs text-gray-400 text-center">
                Tap on map or drag the pin to set exact delivery location
              </p>
            </div>
            
            {/* Location Status */}
            <div className="bg-white/5 rounded-lg p-3 space-y-2">
              {checkoutData.latitude && checkoutData.longitude ? (
                <>
                  <div className="flex items-center gap-2 text-green-400">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium">Location pinned</span>
                  </div>
                  <div className="text-xs text-gray-400 font-mono bg-black/30 p-2 rounded flex items-center justify-between">
                    <span>GPS: {checkoutData.latitude.toFixed(6)}, {checkoutData.longitude.toFixed(6)}</span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`${checkoutData.latitude}, ${checkoutData.longitude}`);
                        toast.success('Coordinates copied!');
                      }}
                      className="text-[#00E5FF] hover:text-[#00E5FF]/80 text-xs"
                    >
                      Copy
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 text-yellow-400">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <span className="text-sm">Use current location or tap on map to pin</span>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowMapPicker(false)}
                className="border-white/20 text-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmMapLocation}
                disabled={!checkoutData.latitude || !checkoutData.longitude}
                className="bg-[#FF4500] hover:bg-[#FF4500]/80 text-white disabled:opacity-50"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Confirm Location
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Available Coupons Dialog */}
      <Dialog open={showAvailableCoupons} onOpenChange={setShowAvailableCoupons}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-md max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Bebas Neue' }}>
              <Tag className="w-5 h-5 text-[#FF4500]" />
              Available Coupons
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-y-auto max-h-[60vh] space-y-3 pr-2">
            {loadingCoupons ? (
              <div className="py-8 text-center">
                <Loader2 className="w-8 h-8 mx-auto text-[#FF4500] animate-spin" />
                <p className="text-gray-400 mt-2">Loading coupons...</p>
              </div>
            ) : availableCoupons.length === 0 ? (
              <div className="py-8 text-center">
                <Tag className="w-12 h-12 mx-auto text-gray-600 mb-3" />
                <p className="text-gray-400">No coupons available right now</p>
                <p className="text-gray-500 text-sm mt-1">Check back later for offers!</p>
              </div>
            ) : (
              availableCoupons.map((coupon, index) => (
                <div 
                  key={index}
                  className={`border rounded-xl p-4 transition-all ${
                    coupon.meets_minimum 
                      ? 'border-[#FF4500]/50 bg-[#FF4500]/5 hover:bg-[#FF4500]/10 cursor-pointer' 
                      : 'border-white/10 bg-white/5 opacity-60'
                  }`}
                  onClick={() => coupon.meets_minimum && applySelectedCoupon(coupon.code)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      {/* Coupon Code */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-[#FF4500] text-white px-3 py-1 rounded-lg font-mono font-bold text-sm">
                          {coupon.code}
                        </span>
                        {coupon.meets_minimum && (
                          <span className="text-green-400 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" /> Applicable
                          </span>
                        )}
                      </div>
                      
                      {/* Description */}
                      <p className="text-white text-sm mb-2">
                        {coupon.description || (
                          coupon.discount_type === 'percentage' 
                            ? `${coupon.discount_value}% OFF${coupon.max_discount ? ` (up to ${formatCurrency(coupon.max_discount)})` : ''}`
                            : `Flat ${formatCurrency(coupon.discount_value)} OFF`
                        )}
                      </p>
                      
                      {/* Details */}
                      <div className="flex flex-wrap gap-2 text-xs">
                        {coupon.min_order_amount > 0 && (
                          <span className={`px-2 py-1 rounded ${coupon.meets_minimum ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                            Min order: {formatCurrency(coupon.min_order_amount)}
                          </span>
                        )}
                        {coupon.potential_discount > 0 && coupon.meets_minimum && (
                          <span className="bg-[#FF4500]/20 text-[#FF4500] px-2 py-1 rounded">
                            Save {formatCurrency(coupon.potential_discount)}
                          </span>
                        )}
                      </div>
                      
                      {/* Not applicable message */}
                      {!coupon.meets_minimum && (
                        <p className="text-red-400 text-xs mt-2">
                          Add {formatCurrency(coupon.min_order_amount - cartTotal)} more to use this coupon
                        </p>
                      )}
                    </div>
                    
                    {/* Apply Button */}
                    {coupon.meets_minimum && (
                      <Button
                        size="sm"
                        className="bg-[#FF4500] hover:bg-[#FF5722] text-white shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          applySelectedCoupon(coupon.code);
                        }}
                      >
                        Apply
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Footer */}
          <div className="border-t border-white/10 pt-4 mt-2">
            <p className="text-gray-500 text-xs text-center">
              Tap on a coupon to apply it to your order
            </p>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Offline Orders Dialog */}
      <Dialog open={showOfflineOrders} onOpenChange={setShowOfflineOrders}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              📦 Offline Orders ({offlineOrders.length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {offlineOrders.length === 0 ? (
              <p className="text-gray-400 text-center py-4">No offline orders</p>
            ) : (
              <>
                <p className="text-sm text-gray-400">
                  These orders were saved when you were offline. They will be synced when you reconnect.
                </p>
                {offlineOrders.map((order) => (
                  <div key={order.id} className="bg-white/5 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">
                        {order.items?.length || 0} items • {formatCurrency(order.total_amount)}
                      </span>
                      <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">
                        {order.payment_method === 'cod' ? 'COD' : 'Online'}
                      </Badge>
                    </div>
                    <div className="text-xs text-gray-500 mb-2">
                      Saved: {new Date(order.created_at).toLocaleString('en-IN')}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => removeOfflineOrder(order.id)}
                        className="text-xs border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        Remove
                      </Button>
                    </div>
                    {order.error && (
                      <p className="text-red-400 text-xs mt-2">Error: {order.error}</p>
                    )}
                  </div>
                ))}
                <Button 
                  onClick={syncOfflineOrders}
                  disabled={syncingOrders || networkStatus === 'offline'}
                  className="w-full bg-blue-500 hover:bg-blue-600"
                >
                  {syncingOrders ? 'Syncing...' : networkStatus === 'offline' ? 'No Connection' : 'Sync All Orders'}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Mobile Install Banner - Fixed at bottom */}
      {!isInstalled && installPrompt && (
        <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-gradient-to-r from-[#FF4500] to-[#FF5722] p-3 z-50 safe-area-inset-bottom">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <img 
                src="/logo.png" 
                alt="Fish on Fire" 
                className="h-10 w-auto object-contain bg-white/20 rounded-lg p-1"
              />
              <div>
                <p className="text-white font-medium text-sm">Install Fish on Fire</p>
                <p className="text-white/80 text-xs">Add to home screen for quick access</p>
              </div>
            </div>
            <Button
              onClick={handleInstallClick}
              size="sm"
              className="bg-white text-[#FF4500] hover:bg-white/90 font-medium"
              data-testid="mobile-install-btn"
            >
              Install
            </Button>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer siteSettings={siteSettings} />

      {/* Order Status Tracker - Shows at bottom when customer has active orders */}
      {isLoggedIn && customer?.phone && (
        <OrderStatusTracker 
          customerPhone={customer.phone} 
          isVisible={true}
        />
      )}
    </div>
  );
};

export default CustomerApp;
